import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, storage } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, query, orderBy, limit, updateDoc, doc, writeBatch, increment, getDoc, deleteDoc, onSnapshot, addDoc, runTransaction } from 'firebase/firestore';
import './AdminDashboard.css';
import Spinner from './Spinner';
import Notification from './Notification';
import { Link, useLocation } from 'react-router-dom';
import {
  isFullAdminFromStorage,
  hasStaffFeature,
  getStaffPermissionsFromStorage,
  hasStaffFeatureForAdminView,
  isViewerFromStorage
} from '../utils/staffAccess';
import {
  showToolsGridFromFeatures,
  showOnlineSectionFromFeatures,
  firstAllowedAdminSubview
} from '../config/staffFeatures';
import TrackingNumberModal from './TrackingNumberModal';
import { orderTrackingWasSent } from '../utils/orderTracking';
import OrderCustomerEmailModal from './OrderCustomerEmailModal';
import OrderCustomerInfo from './OrderCustomerInfo';
import MercadoPagoAdmin from './MercadoPagoAdmin';
import RemitoPrintInline from './RemitoPrintInline';
import ImprimirTicket from './ImprimirTicket';
import { printShippingLabel } from '../utils/shippingLabelPrint';
import { getCustomerPhone, buildWhatsAppUrl } from '../utils/whatsappLink';
import { getCloudFunctionUrl, CONFIG } from '../config';
// import AdminExpenses from './AdminExpenses'; // Comentado porque no se usa actualmente

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) return '0,00';
  return number.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 120000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
};

/** Snapshot/listados del dashboard: sin esto Firestore lee colecciones enteras en tiempo real (muy caro). Pedidos viejos: Historial / Informes. */
const DASHBOARD_QUERY_LIMIT = 500;

const formatPrice = (price) => {
  if (price === null || price === undefined) return '$0.00';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(price);
};

const getOrderCustomerName = (item) =>
  String(item?.nombre || item?.customerName || item?.customerData?.name || '').trim();

/** Normaliza un documento de `contacts` para el panel (fechas y respuestas enviadas). */
const mapContactDocument = (docSnap) => {
  const data = docSnap.data();
  const adminReplies = Array.isArray(data.adminReplies)
    ? data.adminReplies.map((r) => ({
        text: typeof r.text === 'string' ? r.text : '',
        subject: typeof r.subject === 'string' ? r.subject : '',
        hasPdf: !!r.hasPdf,
        sentAt: r.sentAt?.toDate?.() ?? null
      }))
    : [];
  return {
    // Importante: el `id` del doc de Firestore debe prevalecer sobre cualquier campo `id` guardado en data
    ...(data || {}),
    id: docSnap.id,
    timestamp: data.timestamp?.toDate?.() ?? null,
    lastAdminReplyAt: data.lastAdminReplyAt?.toDate?.() ?? null,
    adminReplies,
    subject: typeof data.subject === 'string' ? data.subject : '',
    lastAdminReplyText: typeof data.lastAdminReplyText === 'string' ? data.lastAdminReplyText : ''
  };
};

/** Lista ordenada (cronológica) de respuestas ya enviadas para mostrar en la tarjeta. */
const getContactRepliesForDisplay = (message) => {
  if (Array.isArray(message.adminReplies) && message.adminReplies.length > 0) {
    return [...message.adminReplies].sort((a, b) => {
      const ta = a.sentAt instanceof Date ? a.sentAt.getTime() : 0;
      const tb = b.sentAt instanceof Date ? b.sentAt.getTime() : 0;
      return ta - tb;
    });
  }
  if (message.lastAdminReplyAt || message.lastAdminReplyText) {
    return [{
      text: message.lastAdminReplyText
        || 'El texto de esta respuesta no se guardó en el historial (envíos anteriores).',
      subject: typeof message.lastAdminReplySubject === 'string' ? message.lastAdminReplySubject : '',
      hasPdf: !!message.lastAdminReplyHasPdf,
      sentAt: message.lastAdminReplyAt
    }];
  }
  return [];
};

// Componente de Contador de Notificaciones mejorado
const NotificationBadge = ({ count }) => {
  if (count === 0) return null;

  return (
    <span className="notification-badge">
      <span className="badge-count" data-count={count > 99 ? '99+' : count}>
        {count > 99 ? '99+' : count}
      </span>
    </span>
  );
};

const AdminDashboard = () => {
  const location = useLocation();
  const [notification, setNotification] = useState({ message: '', show: false });
  const showMessage = useCallback((message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  }, []);

  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [view, setView] = useState(''); // Sin vista predeterminada
  const [showArchivedOrders, setShowArchivedOrders] = useState(false);
  const [showArchivedSales, setShowArchivedSales] = useState(false);
  const [showArchivedMessages, setShowArchivedMessages] = useState(false);
  // Filtro de mensajes: null = todos, false = activos, true = archivados
  const [messageFilter, setMessageFilter] = useState(false); // Por defecto mostrar activos
  const [showRemitoModal, setShowRemitoModal] = useState(false);
  const [remitoOrderInfo, setRemitoOrderInfo] = useState(null);
  const [invoiceModalItem, setInvoiceModalItem] = useState(null); // { item, type: 'pedidos' | 'compras_mercadopago' }
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [generatingCreditNote, setGeneratingCreditNote] = useState(false);
  /** Modal rápido NC desde listado (sin abrir el modal completo de factura). */
  const [creditNoteQuickModal, setCreditNoteQuickModal] = useState(null);
  const [invoiceProgress, setInvoiceProgress] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [adminPuntoVenta, setAdminPuntoVenta] = useState(8);
  const [adminInvoiceType, setAdminInvoiceType] = useState('facturaC');
  const [adminInvoiceEmail, setAdminInvoiceEmail] = useState('');
  const [adminInvoiceCuit, setAdminInvoiceCuit] = useState('');
  const [montoMaxSinCuit, setMontoMaxSinCuit] = useState(417288); // AFIP: por encima de este monto es obligatorio CUIT/DNI (config Firestore config/facturacion)
  const [orderActionLoading, setOrderActionLoading] = useState(null); // { id, source, action } mientras se ejecuta una acción en un pedido/venta
  const [orderEmailModal, setOrderEmailModal] = useState(null); // { item, source: 'pedidos' | 'compras_mercadopago' }
  const [trackingModal, setTrackingModal] = useState(null); // { item, source }
  const [searchOrder, setSearchOrder] = useState('');
  const [searchName, setSearchName] = useState('');
  // Estados para notificaciones en tiempo real (badges / push; sin toast en pantalla)
  const [newItemsCount, setNewItemsCount] = useState({ orders: 0, sales: 0, messages: 0 });
  const [notificationsMuted, setNotificationsMuted] = useState(() => {
    return localStorage.getItem('notificationsMuted') === 'true';
  });
  const [listenersInitialized, setListenersInitialized] = useState(false);

  // Referencias para controlar primera carga de forma más robusta
  const isFirstLoadRef = useRef({ orders: true, sales: true, messages: true });

  // Estados para almacenar TODOS los datos sin filtrar
  const [allOrders, setAllOrders] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [allMessages, setAllMessages] = useState([]);

  // Referencias para datos anteriores (sin filtrar)
  const prevOrdersRef = useRef([]);
  const prevSalesRef = useRef([]);
  const prevMessagesRef = useRef([]);

  // Referencias para listeners
  const unsubscribeOrdersRef = useRef(null);
  const unsubscribeSalesRef = useRef(null);
  const unsubscribeMessagesRef = useRef(null);

  // Respuestas IA por mensaje de contacto
  const [aiReplies, setAiReplies] = useState({});
  const [composeMessageOpen, setComposeMessageOpen] = useState(false);
  const [composeMessage, setComposeMessage] = useState({
    name: '',
    email: '',
    subject: '',
    text: '',
    selectedProductIds: [],
    includePdf: false,
    improvingAi: false,
    sending: false,
    error: ''
  });
  const [composeSelectedTemplateId, setComposeSelectedTemplateId] = useState('');
  // Modal de texto IA en pantalla completa
  const [aiReplyFullscreenMessageId, setAiReplyFullscreenMessageId] = useState(null);
  // Catálogo de productos para el picker y la selección en respuestas de contacto
  const [contactProductsLoading, setContactProductsLoading] = useState(false);
  // Plantillas de respuesta de contacto
  const [contactTemplates, setContactTemplates] = useState([]);
  const [contactTemplatesLoading, setContactTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ id: null, name: '', body: '', defaultProductIds: [] });
  const [templateSourceMessageId, setTemplateSourceMessageId] = useState(null);
  // Selector de otros productos (colección completa)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [allContactProducts, setAllContactProducts] = useState([]);
  const [productPickerTargetMessageId, setProductPickerTargetMessageId] = useState(null);
  const [productPickerCategoryFilter, setProductPickerCategoryFilter] = useState('');
  const [productPickerSearch, setProductPickerSearch] = useState('');

  // Sincronizar vista con ?view= y permisos de pestañas del panel
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const viewParam = urlParams.get('view');
    const validViews = ['orders', 'sales', 'messages', 'mercadopago'];
    const full = isFullAdminFromStorage();
    const perms = getStaffPermissionsFromStorage();

    if (viewParam && validViews.includes(viewParam)) {
      if (full || hasStaffFeatureForAdminView(viewParam)) {
        setView(viewParam);
        if (viewParam === 'messages') {
          setMessageFilter(false);
        }
        return;
      }
      const fallback = firstAllowedAdminSubview(perms);
      setView(fallback || '');
      return;
    }

    if (!viewParam && !full) {
      const sub = firstAllowedAdminSubview(perms);
      if (sub) setView(sub);
    }
  }, [location.search]);

  const loadContactTemplates = useCallback(async () => {
    setContactTemplatesLoading(true);
    try {
      const templatesCol = collection(db, 'contactTemplates');
      const snap = await getDocs(templatesCol);
      const list = snap.docs.map(d => ({
        id: d.id,
        ...(d.data() || {})
      }));
      setContactTemplates(list);
    } catch (e) {
      console.error('Error loading contact templates:', e);
      showMessage('Error cargando plantillas de respuesta');
    } finally {
      setContactTemplatesLoading(false);
    }
  }, [showMessage]);

  // Cargar plantillas cuando entramos a la vista de mensajes
  useEffect(() => {
    if (view === 'messages') {
      loadContactTemplates();
    }
  }, [view, loadContactTemplates]);

  // Cargar catálogo completo en vista mensajes (modal de productos y selección en respuestas)
  useEffect(() => {
    if (view !== 'messages' || allContactProducts.length > 0) return;

    let cancelled = false;
    setContactProductsLoading(true);
    const loadAllProducts = async () => {
      try {
        const snap = await getDocs(collection(db, 'products'));
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const withUrlsAll = await Promise.all(
          raw.map(async (p) => {
            const arr = p.images || p.Images;
            const firstFromArray = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
            const imgRef = firstFromArray || p.image;

            let displayImageUrl = null;
            if (imgRef && typeof imgRef === 'string') {
              const s = imgRef.trim();
              if (/^https?:\/\//i.test(s)) {
                displayImageUrl = s;
              } else {
                try {
                  displayImageUrl = await getDownloadURL(storageRef(storage, s));
                } catch (_) {
                  displayImageUrl = null;
                }
              }
            } else if (imgRef && typeof imgRef === 'object' && (imgRef.url || imgRef.src)) {
              displayImageUrl = (imgRef.url || imgRef.src).trim();
            }

            return { ...p, displayImageUrl };
          })
        );
        if (!cancelled) {
          setAllContactProducts(withUrlsAll);
        }
      } catch (e) {
        console.error('Error cargando productos para el picker:', e);
        if (!cancelled) {
          setAllContactProducts([]);
          showMessage('Error cargando productos desde Firestore.');
        }
      } finally {
        if (!cancelled) setContactProductsLoading(false);
      }
    };

    loadAllProducts();

    return () => {
      cancelled = true;
      setContactProductsLoading(false);
    };
  }, [view, allContactProducts.length, showMessage]);

  // Funciones para contar elementos pendientes
  const countPendingOrders = useCallback((ordersList) => {
    return ordersList.filter(order => !order.isConfirmed && order.isActive !== false).length;
  }, []);

  const countPendingSales = useCallback((salesList) => {
    return salesList.filter(sale => (!sale.isConfirmed || !sale.isPaid) && sale.isActive !== false).length;
  }, []);

  const countActiveMessages = useCallback((messagesList) => {
    return messagesList.filter(message => !message.isArchived).length;
  }, []);

  // Imprimir rótulo en ventana nueva (evita conflictos con CSS global y hojas en blanco)
  const handlePrintOrder = (order, type = 'order') => {
    printShippingLabel(order, {
      type,
      onPopupBlocked: () => {
        setNotification({ message: 'Permití ventanas emergentes para imprimir el rótulo.', show: true });
        setTimeout(() => setNotification({ message: '', show: false }), 3000);
      }
    });
  };

  const getNextRemitoNumber = useCallback(async () => {
    const counterRef = doc(db, 'counters', 'remitoCounter');
    const newNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      let current = 0;
      if (snap.exists()) {
        current = Number(snap.data().currentRemitoNumber) || 0;
      }
      const next = current + 1;
      tx.set(counterRef, { currentRemitoNumber: next }, { merge: true });
      return next;
    });
    const padded = String(newNumber).padStart(6, '0');
    return `R-${padded}`;
  }, []);

  const [remitoLoadingId, setRemitoLoadingId] = useState(null);

  // Modal de notas internas por pedido/venta (campo `adminNotes` en Firestore)
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesContext, setNotesContext] = useState(null); // { item, source }
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const openRemitoModal = async (item, source) => {
    // Asegurar número de remito único y persistente
    setRemitoLoadingId(item.id || null);
    let remitoNumber = item.remitoNumber || '';
    try {
      if (!remitoNumber && item.id) {
        remitoNumber = await getNextRemitoNumber();
        const collectionName = source === 'compras_mercadopago' ? 'compras_mercadopago' : 'pedidos';
        const docRef = doc(db, collectionName, item.id);
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(docRef);
          if (!snap.exists()) return;
          if (!snap.data().remitoNumber) {
            tx.update(docRef, { remitoNumber });
          }
        });
      }
    } catch (e) {
      console.error('Error generando número de remito:', e);
    } finally {
      setRemitoLoadingId(null);
    }

    // Mapear al formato esperado por RemitoPrintInline
    const mappedProducts = (item.cartItems || []).map(p => ({
      name: p.model,
      model: p.model,
      price: Number(p.price) || 0,
      quantity: Number(p.quantity) || 0
    }));

    const mappedInfo = {
      orderNumber: item.orderNumber,
      remitoNumber,
      date: item.date,
      products: mappedProducts,
      customerData: {
        name: item.nombre || '',
        phone: item.telefono || '',
        address: `${item.domicilio || ''}, ${item.localidad || ''}, ${item.codigoPostal || ''}, ${item.provincia || ''}, ${item.pais || ''}`,
        cuit: item.cuit || item.cuil || ''
      },
      subtotal: Number(item.subtotal) || 0,
      discountAmount: Number(item.discountAmount) || 0,
      totalPrice: Number(item.totalPrice) || 0,
      transportista: item.transportista || '',
      fechaSalida: item.fechaSalida || '',
      observaciones: item.observaciones || ''
    };

    setRemitoOrderInfo(mappedInfo);
    setShowRemitoModal(true);
  };

  const closeRemitoModal = () => {
    setShowRemitoModal(false);
    setRemitoOrderInfo(null);
  };

  const openCustomerWhatsApp = (item, kind) => {
    const phone = getCustomerPhone(item);
    const name = getOrderCustomerName(item);
    const label = kind === 'sale' ? 'compra' : 'pedido';
    const greeting = name
      ? `Hola ${name}, te escribimos de Priball por tu ${label} #${item.orderNumber}.`
      : `Hola, te escribimos de Priball por tu ${label} #${item.orderNumber}.`;
    const href = buildWhatsAppUrl(phone, greeting);
    if (!href) {
      showMessage('Este registro no tiene un teléfono válido para WhatsApp.');
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openNotesModal = (item, source) => {
    setNotesContext({ item, source });
    setNotesDraft(typeof item.adminNotes === 'string' ? item.adminNotes : '');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    const original = typeof notesContext?.item?.adminNotes === 'string' ? notesContext.item.adminNotes : '';
    if (notesDraft !== original && !window.confirm('Tenés cambios sin guardar. ¿Querés descartarlos?')) {
      return;
    }
    setShowNotesModal(false);
    setNotesContext(null);
    setNotesDraft('');
  };

  const saveNotes = async () => {
    if (!notesContext?.item?.id) return;
    const { item, source } = notesContext;
    const collectionName = source === 'compras_mercadopago' ? 'compras_mercadopago' : 'pedidos';
    setNotesSaving(true);
    try {
      const trimmed = (notesDraft || '').trim();
      await updateDoc(doc(db, collectionName, item.id), {
        adminNotes: trimmed,
        adminNotesUpdatedAt: new Date(),
        adminNotesUpdatedBy: auth.currentUser?.email || ''
      });
      showMessage('Nota guardada');
      setShowNotesModal(false);
      setNotesContext(null);
      setNotesDraft('');
    } catch (error) {
      console.error('Error guardando nota:', error);
      showMessage('Error al guardar la nota');
    } finally {
      setNotesSaving(false);
    }
  };

  // Mapear pedido o venta MP al mismo formato saleInfo que usa LocalSales (para InvoicePrinter e ImprimirTicket)
  const mapOrderOrSaleToSaleInfo = useCallback((item, type) => {
    const products = (item.cartItems || []).map(p => ({
      id: p.id,
      model: p.model,
      name: p.model,
      title: p.model,
      price: Number(p.price) || 0,
      quantity: Number(p.quantity) || 0,
      images: p.image ? [p.image] : []
    }));
    const address = [item.domicilio, item.localidad, item.codigoPostal, item.provincia, item.pais]
      .filter(Boolean).join(', ') || 'Sin domicilio';
    const saleInfo = {
      id: item.id,
      orderNumber: item.orderNumber,
      date: item.date?.seconds ? new Date(item.date.seconds * 1000) : (item.date || new Date()),
      products,
      customerData: {
        name: item.nombre || 'Consumidor final',
        email: item.email || '',
        address,
        city: item.localidad || '',
        postalCode: item.codigoPostal || '',
        province: item.provincia || '',
        country: item.pais || 'Argentina',
        cuit: item.cuit || null,
        cuil: item.cuil || item.cuit || '',
        documentType: item.documentType || (item.cuit ? '80' : '99')  // 80=CUIT, 99=Consumidor final (Factura C)
      },
      subtotal: Number(item.subtotal) || 0,
      discount: item.discount != null ? item.discount : 0,
      discountType: item.discountType || 'percentage',
      discountAmount: Number(item.discountAmount) || 0,
      totalPrice: Number(item.totalPrice) || 0,
      paymentMethods: type === 'compras_mercadopago' ? { mercadopago: true } : { pedidoWeb: true },
      invoiceOptions: item.invoiceOptions || null,
      invoiceData: item.invoiceData || null,
      creditNoteData: item.creditNoteData || null
    };
    if (saleInfo.invoiceData) saleInfo.invoiceData.id = item.id;
    if (saleInfo.creditNoteData) saleInfo.creditNoteData.id = item.id;
    return saleInfo;
  }, []);

  const openInvoiceModal = (item, type) => {
    setInvoiceModalItem({ item, type });
    setAdminInvoiceCuit(item.cuit || '');
  };

  const closeInvoiceModal = () => {
    setInvoiceModalItem(null);
    setInvoiceError('');
    setInvoiceProgress('');
    setGeneratingCreditNote(false);
  };

  // Cargar umbral AFIP (Factura C: monto a partir del cual es obligatorio CUIT/DNI)
  useEffect(() => {
    const loadFacturacionConfig = async () => {
      try {
        const configSnap = await getDoc(doc(db, 'config', 'facturacion'));
        if (configSnap.exists() && configSnap.data().montoMaxSinCuit != null) {
          setMontoMaxSinCuit(Number(configSnap.data().montoMaxSinCuit));
        }
      } catch (e) {
        console.warn('Config facturacion no cargada, usando umbral por defecto:', e);
      }
    };
    loadFacturacionConfig();
  }, []);

  // Generar factura AFIP real (CAE, QR); guardar solo en el pedido/venta original para que los balances muestren la venta en su canal (Pedidos o MercadoPago)
  const generateAfipInvoiceFromAdmin = async (saleInfo) => {
    let url;
    try {
      url = getCloudFunctionUrl('generateAfipInvoiceReal');
    } catch {
      url = '';
    }
    if (!url) {
      showMessage('URL de Cloud Functions no configurada (generateAfipInvoiceReal).');
      return;
    }
    setInvoiceError('');
    const pv = Number(adminPuntoVenta) || 8;
    const totalPrice = Number(saleInfo.totalPrice) || 0;
    const effectiveCuit = (adminInvoiceCuit || saleInfo.customerData?.cuit || '').trim();
    const cuitDigitCount = effectiveCuit.replace(/\D/g, '').length;

    // AFIP: por encima del umbral es obligatorio CUIT/DNI para Factura C; validar antes de llamar a AFIP para no romper flujo
    if (adminInvoiceType === 'facturaC' && totalPrice >= montoMaxSinCuit && cuitDigitCount < 8) {
      setInvoiceError(`Para facturar operaciones de $${formatNumber(montoMaxSinCuit)} o más (AFIP) es obligatorio que el comprador tenga CUIT o DNI. Este pedido no tiene dato fiscal válido. Completá el CUIT/DNI en el campo de abajo o solicitá al cliente que lo ingrese en una próxima compra.`);
      return;
    }

    setGeneratingInvoice(true);
    setInvoiceProgress('Validando datos fiscales...');
    const emailToSend = (adminInvoiceEmail || saleInfo.customerData?.email || '').trim();
    const requestPayload = {
      saleData: {
        orderNumber: Number(saleInfo.orderNumber),
        products: (saleInfo.products || []).map(p => ({
          id: p.id,
          model: p.model,
          name: p.model,
          price: Number(p.price),
          quantity: Number(p.quantity)
        })),
        customerData: {
          name: saleInfo.customerData?.name || 'Consumidor Final',
          email: saleInfo.customerData?.email || '',
          cuit: effectiveCuit || saleInfo.customerData?.cuit || '',
          cuil: effectiveCuit || saleInfo.customerData?.cuil || saleInfo.customerData?.cuit || '',
          dni: saleInfo.customerData?.dni || effectiveCuit || '',
          documentType: saleInfo.customerData?.documentType || (effectiveCuit ? '80' : '99'),
          address: saleInfo.customerData?.address || '',
          city: saleInfo.customerData?.city || '',
          postalCode: saleInfo.customerData?.postalCode || '',
          province: saleInfo.customerData?.province || '',
          country: saleInfo.customerData?.country || 'Argentina'
        },
        subtotal: Number(saleInfo.subtotal),
        discount: saleInfo.discount != null ? Number(saleInfo.discount) : 0,
        discountType: saleInfo.discountType || 'percentage',
        discountAmount: Number(saleInfo.discountAmount || 0),
        totalPrice: Number(saleInfo.totalPrice),
        invoiceOptions: { sendByEmail: Boolean(emailToSend), emailAddress: emailToSend }
      },
      invoiceType: adminInvoiceType,
      puntoVenta: pv,
      sendByEmail: Boolean(emailToSend),
      emailAddress: emailToSend
    };
    try {
      setInvoiceProgress('Conectando con AFIP...');
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      }, 120000);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      if (!result.success || !result.data?.invoice) {
        throw new Error(result.message || result.error || 'No se recibió comprobante de AFIP');
      }
      const invoiceData = result.data.invoice;
      setInvoiceProgress('Guardando comprobante...');
      const collectionName = invoiceModalItem.type === 'pedidos' ? 'pedidos' : 'compras_mercadopago';
      // Guardar invoiceData solo en el documento original (pedidos o compras_mercadopago)
      // para que los balances y analíticas sigan mostrando la venta en su canal correcto (Pedidos o MercadoPago), no en ventas físicas
      const invoiceDataWithId = { ...invoiceData, id: saleInfo.id };
      await updateDoc(doc(db, collectionName, saleInfo.id), { invoiceData: invoiceDataWithId });
      setInvoiceModalItem(prev => prev ? { ...prev, item: { ...prev.item, invoiceData: invoiceDataWithId } } : null);
      setInvoiceProgress('');
      setGeneratingInvoice(false);
      showMessage('Factura AFIP generada con CAE y QR. Ya podés imprimir.');
      if (result.data?.emailSent && emailToSend) {
        showMessage(`Comprobante enviado por email a ${emailToSend}`);
      }
    } catch (err) {
      setInvoiceError(err.message || 'Error al generar factura AFIP');
      setGeneratingInvoice(false);
      setInvoiceProgress('');
      showMessage('Error: ' + (err.message || 'Facturación AFIP'));
    }
  };

  /** Nota de Crédito C total asociada a la Factura C ya emitida (mismo PV; email opcional como en factura). */
  const generateAfipCreditNoteFromAdmin = async (saleInfo, sourceCollectionType = null) => {
    let url;
    try {
      url = getCloudFunctionUrl('generateAfipCreditNoteReal');
    } catch {
      url = '';
    }
    if (!url) {
      showMessage('URL de Cloud Functions no configurada (generateAfipCreditNoteReal).');
      return;
    }
    if (!saleInfo.invoiceData || Number(saleInfo.invoiceData.tipo_comprobante) !== 11) {
      showMessage('Solo podés emitir NC C sobre una Factura C con CAE.');
      return;
    }
    if (saleInfo.creditNoteData?.cae) {
      showMessage('Esta venta ya tiene nota de crédito registrada.');
      return;
    }
    setInvoiceError('');
    const pv = Number(adminPuntoVenta) || 8;
    const emailToSend = (adminInvoiceEmail || saleInfo.customerData?.email || '').trim();
    setGeneratingCreditNote(true);
    setInvoiceProgress('Solicitando Nota de Crédito a AFIP...');
    const requestPayload = {
      saleData: {
        orderNumber: Number(saleInfo.orderNumber),
        products: (saleInfo.products || []).map(p => ({
          id: p.id,
          model: p.model,
          name: p.model,
          price: Number(p.price),
          quantity: Number(p.quantity)
        })),
        customerData: {
          name: saleInfo.customerData?.name || 'Consumidor Final',
          email: saleInfo.customerData?.email || '',
          cuit: saleInfo.customerData?.cuit || '',
          cuil: saleInfo.customerData?.cuil || saleInfo.customerData?.cuit || '',
          dni: saleInfo.customerData?.dni || '',
          documentType: saleInfo.customerData?.documentType || '99',
          address: saleInfo.customerData?.address || '',
          city: saleInfo.customerData?.city || '',
          postalCode: saleInfo.customerData?.postalCode || '',
          province: saleInfo.customerData?.province || '',
          country: saleInfo.customerData?.country || 'Argentina'
        },
        subtotal: Number(saleInfo.subtotal),
        discount: saleInfo.discount != null ? Number(saleInfo.discount) : 0,
        discountType: saleInfo.discountType || 'percentage',
        discountAmount: Number(saleInfo.discountAmount || 0),
        totalPrice: Number(saleInfo.totalPrice),
        invoiceData: saleInfo.invoiceData,
        invoiceOptions: { sendByEmail: Boolean(emailToSend), emailAddress: emailToSend }
      },
      originalInvoice: saleInfo.invoiceData,
      puntoVenta: pv,
      sendByEmail: Boolean(emailToSend),
      emailAddress: emailToSend
    };
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      }, 120000);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      if (!result.success || !result.data?.creditNote) {
        throw new Error(result.message || result.error || 'No se recibió nota de crédito de AFIP');
      }
      const creditNoteData = result.data.creditNote;
      setInvoiceProgress('Guardando nota de crédito...');
      const resolvedSource = sourceCollectionType || invoiceModalItem?.type;
      if (resolvedSource !== 'pedidos' && resolvedSource !== 'compras_mercadopago') {
        throw new Error('No se pudo determinar la colección (pedidos / MercadoPago) para guardar la NC.');
      }
      const collectionName = resolvedSource === 'pedidos' ? 'pedidos' : 'compras_mercadopago';
      const creditNoteWithId = { ...creditNoteData, id: saleInfo.id };
      await updateDoc(doc(db, collectionName, saleInfo.id), {
        creditNoteData: creditNoteWithId,
        invoiceVoided: true
      });
      if (resolvedSource === 'pedidos') {
        setOrders(prev => prev.map(o => (o.id === saleInfo.id ? { ...o, creditNoteData: creditNoteWithId } : o)));
      } else {
        setSales(prev => prev.map(s => (s.id === saleInfo.id ? { ...s, creditNoteData: creditNoteWithId } : s)));
      }
      setInvoiceModalItem(prev =>
        prev && prev.item.id === saleInfo.id ? { ...prev, item: { ...prev.item, creditNoteData: creditNoteWithId } } : prev
      );
      setCreditNoteQuickModal(prev =>
        prev && prev.item.id === saleInfo.id ? { ...prev, item: { ...prev.item, creditNoteData: creditNoteWithId } } : prev
      );
      setInvoiceProgress('');
      setGeneratingCreditNote(false);
      showMessage('Nota de crédito AFIP generada. Podés imprimir o revisar el email.');
      if (result.data?.emailSent && emailToSend) {
        showMessage(`Nota de crédito enviada por email a ${emailToSend}`);
      }
      setCreditNoteQuickModal(null);
    } catch (err) {
      setInvoiceError(err.message || 'Error al generar nota de crédito');
      setGeneratingCreditNote(false);
      setInvoiceProgress('');
      showMessage('Error NC: ' + (err.message || 'AFIP'));
    }
  };

  // Imprimir solo el ticket en una ventana nueva (evita hojas en blanco); estilos autocontenidos para que la vista previa funcione en todos los equipos
  const printTicketInNewWindow = useCallback((ticketWrapperEl) => {
    if (!ticketWrapperEl || !ticketWrapperEl.innerHTML) return;
    const criticalTicketCss = `
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.ticket-wrapper{width:100%;max-width:100%;font-family:Arial,sans-serif;background:#fff;visibility:visible}
.ticket-content{background:#fff;padding:0;color:#1a1a1a;font-size:12px;line-height:1.35;display:flex;flex-direction:column;min-height:0;visibility:visible}
.ticket-content-inner{padding:16px;flex:1;display:flex;flex-direction:column;visibility:visible}
.ticket-type-banner{display:flex;align-items:center;justify-content:center;width:100%;font-size:18px;font-weight:700;padding:10px 16px;background:#b8d4e8;color:#1a1a1a;border-bottom:2px solid #000}
.ticket-sep{border:none;border-top:1px solid #000;margin:10px 0}
.ticket-header-three-cols{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;font-size:10px;align-items:center;margin-bottom:0}
.ticket-col-emisor{padding:6px 0;min-width:0}
.ticket-logo-small{max-width:192px;max-height:36px;width:auto;height:auto;display:block;margin-bottom:6px;object-fit:contain}
.ticket-col-emisor p{margin:2px 0;line-height:1.35;color:#333}
.ticket-col-tipo{display:flex;align-items:center;justify-content:center;min-width:0}
.ticket-tipo-box{background:#e0e0e0;color:#000;width:64px;min-height:72px;padding:8px 4px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;flex-shrink:0;border:1px solid #bbb;border-radius:6px}
.ticket-tipo-letra{display:block;font-size:28px;font-weight:700;line-height:1}
.ticket-tipo-codigo{display:block;font-size:11px;margin-top:4px;font-weight:600}
.ticket-tipo-monotributista{display:block;font-size:9px;margin-top:2px}
.ticket-col-datos{text-align:right;min-width:0}
.ticket-col-datos p{margin:2px 0;line-height:1.35;color:#333}
.ticket-factura-title{font-weight:700;font-size:13px;margin-bottom:4px!important}
.ticket-order{margin-bottom:0}
.order-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:4px;font-size:11px;align-items:baseline}
.order-row strong{grid-column:1}
.order-row span{grid-column:2;text-align:right}
.ticket-customer,.ticket-payment,.ticket-invoice{margin:10px 0}
.customer-row,.payment-row,.invoice-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;align-items:center;margin:5px 0;font-size:11px}
.products-header2{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-weight:700;font-size:11px;background:#b8d4e8;border:1px solid #000;margin-bottom:0}
.product-row{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-size:11px;border-bottom:1px solid #000}
.ticket-summary{margin-top:15px}
.summary-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:5px;align-items:baseline}
.summary-row span:first-child{grid-column:1}
.summary-row span:last-child{grid-column:2;text-align:right}
.total-row{padding-top:10px;border-top:2px solid #ddd;font-size:16px}
.total-row strong{grid-column:1 / -1;text-align:right;color:#1a1a1a;font-size:14px}
.ticket-footer-afip{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;text-align:left;background:#fff;padding-left:20px;padding-right:20px}
.ticket-qr img{display:block;border:1px solid #ddd}
.ticket-qr-label{font-size:10px;margin:0;color:#666}
.ticket-thanks{margin-top:6px!important;font-weight:bold}
@media print{.ticket-actions,.print-btn{display:none!important}body,body *{visibility:visible}body{margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}body .ticket-wrapper{display:block!important;visibility:visible!important;position:relative!important;left:auto;top:auto;width:100%!important;max-width:210mm!important;margin:0;padding:0;background:#fff!important}body .ticket-wrapper *{visibility:visible!important}body .ticket-wrapper .ticket-header-three-cols{display:grid!important}}
`;
    let html = ticketWrapperEl.innerHTML;
    const logoImg = ticketWrapperEl.querySelector('.ticket-logo-small');
    const logoSrc = logoImg?.getAttribute('src');
    if (logoSrc && !logoSrc.startsWith('data:')) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let resolved = false;
      const runPrint = () => {
        if (resolved) return;
        resolved = true;
        openAndPrint();
      };
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext('2d').drawImage(img, 0, 0);
          html = html.split(logoSrc).join(c.toDataURL('image/png'));
        } catch (_) {}
        runPrint();
      };
      img.onerror = runPrint;
      img.src = logoSrc;
      setTimeout(runPrint, 1500);
    } else {
      openAndPrint();
    }
    function openAndPrint() {
      const win = window.open('', '_blank');
      if (!win) {
        setNotification({ message: 'Permití ventanas emergentes para imprimir.', show: true });
        setTimeout(() => setNotification({ message: '', show: false }), 3000);
        return;
      }
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Comprobante</title><style>${criticalTicketCss}</style></head><body>${html}</body></html>`);
      win.document.close();
      let printed = false;
      const doPrint = () => {
        if (printed) return;
        printed = true;
        win.focus();
        win.print();
        setTimeout(() => win.close(), 400);
      };
      const waitThenPrint = () => {
        if (win.document.readyState !== 'complete') { win.onload = waitThenPrint; return; }
        const imgs = win.document.querySelectorAll('img');
        if (Array.from(imgs).every((i) => i.complete)) {
          setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
          return;
        }
        let waited = 0;
        const check = () => {
          if (Array.from(imgs).every((i) => i.complete) || waited >= 5000) {
            setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
            return;
          }
          waited += 200;
          setTimeout(check, 200);
        };
        setTimeout(check, 200);
      };
      win.onload = waitThenPrint;
      setTimeout(waitThenPrint, 2500);
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    // Verificar si las notificaciones están disponibles de forma más robusta
    if (typeof window === 'undefined' || !window.Notification) {
      console.log('Las notificaciones no están soportadas en este entorno');
      return 'unavailable';
    }

    // Obtener el estado actual de permisos de forma segura
    let currentPermission;
    try {
      currentPermission = window.Notification.permission;
    } catch (error) {
      console.log('Error obteniendo permisos de notificación:', error);
      return 'error';
    }

    if (currentPermission === 'default') {
      try {
        const permission = await window.Notification.requestPermission();
        return permission;
      } catch (error) {
        console.log('Error solicitando permisos:', error);
        return 'error';
      }
    } else if (currentPermission === 'denied') {
      showMessage('Notificaciones bloqueadas. Actívalas en la configuración del navegador.');
      return currentPermission;
    } else if (currentPermission === 'granted') {
      return currentPermission;
    } else {
      // Para permisos undefined, intentar solicitar
      try {
        if (window.Notification && typeof window.Notification.requestPermission === 'function') {
          const permission = await window.Notification.requestPermission();
          return permission;
        } else {
          return 'unavailable';
        }
      } catch (error) {
        console.log('Error en solicitud de permisos de respaldo:', error);
        return 'error';
      }
    }
  }, [showMessage]);

  // Función para actualizar el favicon con badge (VERSIÓN MEJORADA)
  const updateFaviconBadge = useCallback((count) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 64;
    canvas.height = 64;

    img.onload = () => {
      // Dibujar el favicon original
      ctx.drawImage(img, 0, 0, 64, 64);

      // Si hay notificaciones, agregar badge
      if (count > 0) {
        // Sombra del badge para mejor contraste
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.beginPath();
        ctx.arc(50, 16, 18, 0, 2 * Math.PI);
        ctx.fill();

        // Badge rojo principal (MÁS GRANDE)
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.arc(48, 14, 15, 0, 2 * Math.PI);  // Radio aumentado de 12 a 15px
        ctx.fill();

        // Texto del badge (MÁS GRANDE Y LEGIBLE)
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 20px Arial';          // Fuente aumentada de 14px a 20px
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const text = count > 99 ? '99+' : count.toString();
        
        // Sombra del texto para mejor legibilidad
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillText(text, 49, 15);
        
        // Texto principal
        ctx.fillStyle = '#ffffff';
        ctx.fillText(text, 48, 14);            // Posición ajustada para el nuevo tamaño
      }

      // Actualizar el favicon
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = canvas.toDataURL();
      document.getElementsByTagName('head')[0].appendChild(link);
    };

    img.src = '/favicon.ico';
  }, []);

  // Función para actualizar el badge de la aplicación
  const updateAppBadge = useCallback(() => {
    const totalNotifications = newItemsCount.orders + newItemsCount.sales + newItemsCount.messages;

    // Badge API para PWA
    if ('setAppBadge' in navigator) {
      if (totalNotifications > 0) {
        navigator.setAppBadge(totalNotifications);
      } else {
        navigator.clearAppBadge();
      }
    }

    // Favicon dinámico para navegadores
    updateFaviconBadge(totalNotifications);
  }, [newItemsCount, updateFaviconBadge]);

  const playNotificationSound = useCallback(() => {
    if (notificationsMuted) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Error playing notification sound:', error);
    }
  }, [notificationsMuted]);

  const handleGenerateAiReply = async (message) => {
    if (!message || !message.message || !message.email) {
      showMessage('El mensaje no tiene datos suficientes para generar una respuesta.');
      return;
    }
    setAiReplies(prev => ({
      ...prev,
      [message.id]: {
        ...(prev[message.id] || {}),
        loading: true,
        error: null
      }
    }));
    let url = '';
    try {
      url = getCloudFunctionUrl('generateContactReply');
    } catch {
      url = '';
    }
    if (!url) {
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          loading: false,
          error: 'No hay URL configurada para generar respuestas IA.'
        }
      }));
      showMessage('No hay URL configurada para generar respuestas IA.');
      return;
    }
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageId: message.id,
          messageText: message.message,
          name: message.name,
          email: message.email
        })
      }, 60000);
      const data = await response.json();
      if (!response.ok || !data.success) {
        const errorMsg = data?.message || data?.error || `Error HTTP ${response.status}`;
        throw new Error(errorMsg);
      }
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          loading: false,
          error: null,
          text: (data.data?.replyText || '').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
        }
      }));
      showMessage('Respuesta IA generada. Revisala antes de enviar.');
    } catch (error) {
      console.error('Error generando respuesta IA:', error);
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          loading: false,
          error: error.message || 'Error generando respuesta IA'
        }
      }));
      showMessage('Error generando respuesta IA.');
    }
  };

  const openNewTemplateModalWithDefaults = ({ baseText, defaultProducts, sourceMessageId = null }) => {
    setTemplateForm({ id: null, name: '', body: baseText, defaultProductIds: defaultProducts });
    setTemplateSourceMessageId(sourceMessageId);
    setTemplateModalOpen(true);
  };

  const openNewTemplateModal = (message) => {
    const baseText = (aiReplies[message.id]?.text || '').trim();
    const defaultProducts = Array.isArray(aiReplies[message.id]?.selectedProductIds)
      ? [...aiReplies[message.id].selectedProductIds]
      : [];
    openNewTemplateModalWithDefaults({ baseText, defaultProducts, sourceMessageId: message.id });
  };

  const openEditTemplateModal = () => {
    if (!selectedTemplateId) return;
    const tpl = contactTemplates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    setTemplateForm({
      id: tpl.id,
      name: tpl.name || '',
      body: tpl.body || '',
      defaultProductIds: Array.isArray(tpl.defaultProductIds) ? tpl.defaultProductIds : []
    });
    setTemplateSourceMessageId(null);
    setTemplateModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    const name = (templateForm.name || '').trim();
    const body = (templateForm.body || '').trim();
    if (!name || !body) {
      showMessage('Completá nombre y contenido de la plantilla.');
      return;
    }
    try {
      // Si es una plantilla nueva y tiene mensaje de origen, tomamos los productos seleccionados
      let finalDefaultProductIds = Array.isArray(templateForm.defaultProductIds)
        ? [...templateForm.defaultProductIds]
        : [];
      if (!templateForm.id && templateSourceMessageId && aiReplies[templateSourceMessageId]) {
        const srcSelected = aiReplies[templateSourceMessageId].selectedProductIds;
        if (Array.isArray(srcSelected) && srcSelected.length > 0) {
          finalDefaultProductIds = [...srcSelected];
        }
      }

      const colRef = collection(db, 'contactTemplates');
      if (templateForm.id) {
        await updateDoc(doc(db, 'contactTemplates', templateForm.id), {
          name,
          body,
          defaultProductIds: finalDefaultProductIds,
          updatedAt: new Date()
        });
      } else {
        const docRef = await addDoc(colRef, {
          name,
          body,
          defaultProductIds: finalDefaultProductIds,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setSelectedTemplateId(docRef.id);
      }
      await loadContactTemplates();
      setTemplateModalOpen(false);
      setTemplateSourceMessageId(null);
      showMessage('Plantilla guardada.');
    } catch (e) {
      console.error('Error saving template:', e);
      showMessage('Error guardando plantilla');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateForm.id) return;
    const confirmed = window.confirm('¿Eliminar esta plantilla de forma permanente?');
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'contactTemplates', templateForm.id));
      if (selectedTemplateId === templateForm.id) {
        setSelectedTemplateId('');
      }
      await loadContactTemplates();
      setTemplateModalOpen(false);
      showMessage('Plantilla eliminada.');
    } catch (e) {
      console.error('Error deleting template:', e);
      showMessage('Error eliminando plantilla');
    }
  };

  const handleSendContactReply = async (message, { includePdf }) => {
    if (!message || !message.email) {
      showMessage('El mensaje no tiene email válido.');
      return;
    }
    const aiState = aiReplies[message.id] || {};
    const textToSend = (aiState.text || '').trim();
    if (!textToSend) {
      showMessage('No hay texto de respuesta para enviar.');
      return;
    }

    setAiReplies(prev => ({
      ...prev,
      [message.id]: {
        ...(prev[message.id] || {}),
        sending: true,
        sendError: null
      }
    }));

    let url = '';
    try {
      url = getCloudFunctionUrl('sendContactReply');
    } catch {
      url = '';
    }
    if (!url) {
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          sending: false,
          sendError: 'No hay URL configurada para enviar respuestas.'
        }
      }));
      showMessage('No hay URL configurada para enviar respuestas.');
      return;
    }

    try {
      // Calcular qué productos se van a incluir (según selección en la UI)
      let selectedProductIds = [];
      if (Array.isArray(aiState.selectedProductIds) && aiState.selectedProductIds.length > 0) {
        selectedProductIds = [...aiState.selectedProductIds];
      }
      const subject = (message.subject || '').trim()
        ? `Re: ${message.subject}`
        : 'Respuesta a tu consulta | Priball';

      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: message.id,
          to: message.email,
          name: message.name,
          originalMessage: message.message,
          replyText: textToSend,
          subject,
          includePdf: !!includePdf,
          selectedProductIds
        })
      }, includePdf ? 90000 : 60000);
      const data = await response.json();
      if (!response.ok || !data.success) {
        const errorMsg = data?.message || data?.error || `Error HTTP ${response.status}`;
        throw new Error(errorMsg);
      }
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          sending: false,
          sendError: null
        }
      }));
      showMessage(includePdf ? 'Presupuesto enviado por email.' : 'Respuesta enviada por email.');
    } catch (error) {
      console.error('Error enviando respuesta de contacto:', error);
      setAiReplies(prev => ({
        ...prev,
        [message.id]: {
          ...(prev[message.id] || {}),
          sending: false,
          sendError: error.message || 'Error enviando respuesta'
        }
      }));
      showMessage('Error enviando la respuesta.');
    }
  };

  const handleImproveComposeMessageWithAi = async () => {
    const currentText = (composeMessage.text || '').trim();
    if (!currentText) {
      setComposeMessage((prev) => ({
        ...prev,
        error: 'Escribí un mensaje antes de mejorarlo con IA.'
      }));
      return;
    }

    let url = '';
    try {
      url = getCloudFunctionUrl('generateContactReply');
    } catch {
      url = '';
    }
    if (!url) {
      setComposeMessage((prev) => ({
        ...prev,
        error: 'No hay URL configurada para generar respuestas IA.'
      }));
      showMessage('No hay URL configurada para generar respuestas IA.');
      return;
    }

    setComposeMessage((prev) => ({ ...prev, improvingAi: true, error: '' }));
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageId: '__compose__',
          mode: 'improve',
          messageText: currentText,
          name: (composeMessage.name || '').trim() || 'Cliente',
          email: (composeMessage.email || '').trim() || 'sin-email@priball.com'
        })
      }, 60000);

      const data = await response.json();
      if (!response.ok || !data.success) {
        const errorMsg = data?.message || data?.error || `Error HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      const improvedText = (data.data?.replyText || '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .trim();

      if (!improvedText) {
        throw new Error('La IA no devolvió texto para mejorar.');
      }

      setComposeMessage((prev) => ({
        ...prev,
        text: improvedText,
        improvingAi: false,
        error: ''
      }));
      showMessage('Mensaje mejorado con IA. Revisalo antes de enviar.');
    } catch (error) {
      console.error('Error mejorando mensaje con IA:', error);
      setComposeMessage((prev) => ({
        ...prev,
        improvingAi: false,
        error: error.message || 'Error mejorando mensaje con IA'
      }));
      showMessage('Error mejorando el mensaje con IA.');
    }
  };

  const handleSendNewContactMessage = async () => {
    const email = (composeMessage.email || '').trim();
    const subject = (composeMessage.subject || '').trim();
    const text = (composeMessage.text || '').trim();
    const name = (composeMessage.name || '').trim() || email;
    const selectedProductIds = Array.isArray(composeMessage.selectedProductIds)
      ? [...composeMessage.selectedProductIds]
      : [];
    const includePdf = !!composeMessage.includePdf;

    if (!email || !subject || !text) {
      setComposeMessage((prev) => ({
        ...prev,
        error: 'Completá email, asunto y mensaje.'
      }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setComposeMessage((prev) => ({
        ...prev,
        error: 'Ingresá un email válido.'
      }));
      return;
    }

    let url = '';
    try {
      url = getCloudFunctionUrl('sendContactReply');
    } catch {
      url = '';
    }
    if (!url) {
      setComposeMessage((prev) => ({
        ...prev,
        error: 'No hay URL configurada para enviar respuestas.'
      }));
      showMessage('No hay URL configurada para enviar respuestas.');
      return;
    }

    setComposeMessage((prev) => ({ ...prev, sending: true, error: '' }));

    const originalMessage = `Mensaje iniciado desde el panel admin. Asunto: ${subject}`;
    const sentAt = new Date();
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          name,
          originalMessage,
          replyText: text,
          subject,
          templateType: 'business',
          includePdf,
          selectedProductIds
        })
      }, includePdf ? 90000 : 60000);

      const data = await response.json();
      if (!response.ok || !data.success) {
        const errorMsg = data?.message || data?.error || `Error HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      await addDoc(collection(db, 'contacts'), {
        name,
        email,
        subject,
        message: originalMessage,
        isOutbound: true,
        isArchived: false,
        timestamp: sentAt,
        lastAdminReplyAt: sentAt,
        lastAdminReplyHasPdf: includePdf,
        lastAdminReplySubject: subject,
        lastAdminReplyText: text,
        adminReplies: [
          {
            text,
            subject,
            hasPdf: includePdf,
            sentAt
          }
        ]
      });

      setComposeMessage({
        name: '',
        email: '',
        subject: '',
        text: '',
        selectedProductIds: [],
        includePdf: false,
        improvingAi: false,
        sending: false,
        error: ''
      });
      setComposeSelectedTemplateId('');
      setComposeMessageOpen(false);
      showMessage(includePdf ? 'Mensaje y presupuesto enviados por email.' : 'Mensaje enviado por email.');
    } catch (error) {
      console.error('Error enviando nuevo mensaje de contacto:', error);
      setComposeMessage((prev) => ({
        ...prev,
        sending: false,
        error: error.message || 'Error enviando mensaje'
      }));
      showMessage('Error enviando el mensaje.');
    }
  };

  const changeView = useCallback((newView) => {
    setView(newView);
    // Si cambiamos a la vista de mensajes, resetear el filtro a activos por defecto
    if (newView === 'messages') {
      setMessageFilter(false);
    }
    // Actualizar URL sin recargar la página
    const url = new URL(window.location);
    if (newView) {
      url.searchParams.set('view', newView);
    } else {
      url.searchParams.delete('view');
    }
    window.history.pushState({}, '', url);
  }, []);
  
  const showBrowserNotification = useCallback((title, body, icon = '🔔') => {
    if (notificationsMuted) return;

    if (typeof window === 'undefined' || !window.Notification) {
      return;
    }

    let permission;
    try {
      permission = window.Notification.permission;
    } catch (error) {
      return;
    }

    if (permission === 'granted') {
      try {
        const notification = new window.Notification(title, {
          body,
          icon: `/favicon.ico`,
          badge: `/logo192.png`,
          requireInteraction: false,
          silent: false
        });

        setTimeout(() => notification.close(), 5000);
      } catch (error) {
        console.error('Error mostrando notificación del navegador:', error);
      }
    } else if (permission === 'default' || permission === undefined || permission === null) {
      requestNotificationPermission().then(newPermission => {
        if (newPermission === 'granted') {
          showBrowserNotification(title, body, icon);
        }
      });
    }
  }, [notificationsMuted, requestNotificationPermission]);

// 🆕 NUEVA: Función para notificaciones móviles push mejoradas
const showMobileNotification = useCallback((title, body, options = {}) => {
  if (notificationsMuted || typeof window === 'undefined' || !window.Notification) {
    return;
  }

    const notificationOptions = {
      body,
      icon: '/logo192.png',
      badge: '/favicon.ico',
      tag: options.tag || 'priball-notification',
      renotify: true,
      requireInteraction: false,
      silent: false,
      vibrate: [200, 100, 200],
      data: {
        url: options.url || '/admin',
        timestamp: Date.now()
      },
      actions: [
        {
          action: 'view',
          title: '👁️ Ver',
          icon: '/favicon.ico'
        },
        {
          action: 'dismiss',
          title: '❌ Cerrar',
          icon: '/favicon.ico'
        }
      ]
    };

    if (window.Notification.permission === 'granted') {
      try {
        const notification = new window.Notification(title, notificationOptions);
        
        notification.onclick = function(event) {
          event.preventDefault();
          window.focus();
          
          if (options.url && options.url.includes('view=')) {
            const urlParams = new URLSearchParams(options.url.split('?')[1]);
            const view = urlParams.get('view');
            if (view) {
              changeView(view);
            }
          }
          
          notification.close();
        };

        setTimeout(() => notification.close(), 8000);

      } catch (error) {
        console.error('Error mostrando notificación móvil:', error);
      }
    }
}, [notificationsMuted, changeView]);

  // 🆕 NUEVA: Función que detecta dispositivo y envía AMBAS notificaciones
  const showUniversalNotification = useCallback((title, body, options = {}) => {
    if (notificationsMuted) return;

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;

    // SIEMPRE mostrar notificación del navegador (tu sistema original)
    showBrowserNotification(title, body);

    // ADICIONAL: Si es móvil o PWA instalada, mostrar también push mejorada
    if (isMobile || isStandalone) {
      showMobileNotification(title, body, options);
      
      if ('vibrate' in navigator && isMobile) {
        navigator.vibrate([300, 100, 300]);
      }
    }

    console.log(`📱 Notificación enviada - Móvil: ${isMobile}, PWA: ${isStandalone}`);
  }, [notificationsMuted, showBrowserNotification, showMobileNotification]);

  // 🆕 NUEVA: Funciones específicas para cada tipo
  const notifyNewOrder = useCallback((order) => {
    showUniversalNotification(
      '🆕 Nuevo Pedido Recibido',
      `Pedido #${order.orderNumber} de ${order.nombre} por ${formatPrice(order.totalPrice)}`,
      {
        tag: `order-${order.id}`,
        url: '/admin?view=orders'
      }
    );
  }, [showUniversalNotification]);

  const notifyNewSale = useCallback((sale) => {
    showUniversalNotification(
      '💰 Nueva Venta MercadoPago',
      `Venta #${sale.orderNumber} de ${sale.nombre} por ${formatPrice(sale.totalPrice)}`,
      {
        tag: `sale-${sale.id}`,
        url: '/admin?view=sales'
      }
    );
  }, [showUniversalNotification]);

  const notifyNewMessage = useCallback((message) => {
    showUniversalNotification(
      '📧 Nuevo Mensaje de Contacto',
      `${message.name} (${message.email}) te ha enviado un mensaje`,
      {
        tag: `message-${message.id}`,
        url: '/admin?view=messages'
      }
    );
  }, [showUniversalNotification]);

  // // 🆕 NUEVA: Función de prueba
  // const testAllNotifications = useCallback(() => {
  //   console.log('🧪 Probando sistema dual de notificaciones...');
    
  //   showBrowserNotification(
  //     '🌐 Test Navegador',
  //     'Esta es una notificación del navegador (tu sistema original)'
  //   );
    
  //   showMobileNotification(
  //     '📱 Test Móvil Push',
  //     'Esta es una notificación push mejorada para móvil',
  //     { tag: 'test-mobile' }
  //   );
    
  //   setTimeout(() => {
  //     showUniversalNotification(
  //       '🎯 Test Sistema Dual',
  //       'Esta usa automáticamente el mejor método según tu dispositivo',
  //       { tag: 'test-universal' }
  //     );
  //   }, 2000);
    
  // }, [showBrowserNotification, showMobileNotification, showUniversalNotification]);

  // Actualizar badge cuando cambian las notificaciones
  useEffect(() => {
    updateAppBadge();
  }, [updateAppBadge]);

  const toggleNotifications = () => {
    const newMutedState = !notificationsMuted;
    setNotificationsMuted(newMutedState);
    localStorage.setItem('notificationsMuted', newMutedState.toString());
    showMessage(newMutedState ? 'Notificaciones silenciadas' : 'Notificaciones activadas');

    // Si se activan las notificaciones, solicitar permisos
    if (!newMutedState) {
      requestNotificationPermission();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showMessage('Email copiado al portapapeles');
    }).catch(() => {
      showMessage('Error al copiar el email');
    });
  };

  // Aplicar filtros a los datos completos
  useEffect(() => {
    const filteredOrders = allOrders.filter(order =>
      (order.isActive !== false) === !showArchivedOrders
    );
    setOrders(filteredOrders);
  }, [allOrders, showArchivedOrders]);

  useEffect(() => {
    const filteredSales = allSales.filter(sale =>
      (sale.isActive !== false) === !showArchivedSales
    );
    setSales(filteredSales);
  }, [allSales, showArchivedSales]);

  useEffect(() => {
    const filteredMessages = allMessages.filter(message => {
      const isArchived = message.isArchived || false;
      // Si messageFilter es null, mostrar todos; si es false mostrar activos, si es true mostrar archivados
      if (messageFilter === null) {
        return true; // Mostrar todos
      }
      return messageFilter ? isArchived : !isArchived;
    });
    setMessages(filteredMessages);
    // Sincronizar showArchivedMessages con messageFilter para el botón de toggle
    setShowArchivedMessages(messageFilter === true);
  }, [allMessages, messageFilter]);

  

  const setupOrdersListener = useCallback(() => {
    const ordersCollection = collection(db, 'pedidos');
    const ordersQuery = query(ordersCollection, orderBy('date', 'desc'), limit(DASHBOARD_QUERY_LIMIT));

    unsubscribeOrdersRef.current = onSnapshot(ordersQuery, (snapshot) => {
      const ordersList = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          orderNumber: data.orderNumber,
          ...data,
          date: data.date?.toDate(),
          cartItems: data.cartItems?.map(item => ({
            ...item,
            image: item.images && item.images[0] ? item.images[0] : ''
          })),
          isConfirmed: data.isConfirmed || false,
          isPaid: data.isPaid === true
        };
      });

      // Solo procesar notificaciones si NO es la primera carga
      if (!isFirstLoadRef.current.orders) {
        const previousOrders = [...prevOrdersRef.current];

        const newOrders = ordersList.filter(newOrder => {
          const existsInPrevious = previousOrders.some(prevOrder => prevOrder.id === newOrder.id);
          const isActive = newOrder.isActive !== false;

          return !existsInPrevious && isActive;
        });

        if (newOrders.length > 0) {
          newOrders.forEach(order => {
            // 🔄 CAMBIO: Usar sistema dual en vez de solo navegador
            notifyNewOrder(order);
            playNotificationSound();
          });
        }
      } else {
        isFirstLoadRef.current.orders = false;
      }

      // Actualizar contador de badges
      if (view !== 'orders') {
        const activeOrders = ordersList.filter(o => o.isActive !== false);
        setNewItemsCount(prev => ({ ...prev, orders: countPendingOrders(activeOrders) }));
      }

      prevOrdersRef.current = ordersList;
      setAllOrders(ordersList);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to orders: ", error);
      showMessage('Error escuchando pedidos');
      setLoading(false);
    });
  }, [view, notifyNewOrder, playNotificationSound, countPendingOrders, showMessage]);

  const setupSalesListener = useCallback(() => {
    const salesCollection = collection(db, 'compras_mercadopago');
    const salesQuery = query(salesCollection, orderBy('date', 'desc'), limit(DASHBOARD_QUERY_LIMIT));

    unsubscribeSalesRef.current = onSnapshot(salesQuery, (snapshot) => {
      const salesList = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          orderNumber: data.orderNumber,
          ...data,
          date: data.date?.toDate(),
          cartItems: data.cartItems?.map(item => ({
            ...item,
            image: item.images && item.images[0] ? item.images[0] : ''
          })),
          isPaid: data.isPaid || false,
          isConfirmed: data.isConfirmed || false
        };
      });

      // Solo procesar notificaciones si NO es la primera carga
      if (!isFirstLoadRef.current.sales) {
        const previousSales = [...prevSalesRef.current];

        const newSales = salesList.filter(newSale => {
          const existsInPrevious = previousSales.some(prevSale => prevSale.id === newSale.id);
          const isActive = newSale.isActive !== false;

          return !existsInPrevious && isActive;
        });

        if (newSales.length > 0) {
          newSales.forEach(sale => {
            // 🔄 CAMBIO: Usar sistema dual en vez de solo navegador
            notifyNewSale(sale);
            playNotificationSound();
          });
        }
      } else {
        isFirstLoadRef.current.sales = false;
      }

      // Actualizar contador de badges
      if (view !== 'sales') {
        const activeSales = salesList.filter(s => s.isActive !== false);
        setNewItemsCount(prev => ({ ...prev, sales: countPendingSales(activeSales) }));
      }

      prevSalesRef.current = salesList;
      setAllSales(salesList);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to sales: ", error);
      showMessage('Error escuchando ventas');
      setLoading(false);
    });
  }, [view, notifyNewSale, playNotificationSound, countPendingSales, showMessage]);

  const setupMessagesListener = useCallback(() => {
    const messagesCollection = collection(db, 'contacts');
    const messagesQuery = query(messagesCollection, orderBy('timestamp', 'desc'), limit(DASHBOARD_QUERY_LIMIT));

    unsubscribeMessagesRef.current = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList = snapshot.docs.map(mapContactDocument);

      // Solo procesar notificaciones si NO es la primera carga
      if (!isFirstLoadRef.current.messages) {
        const previousMessages = [...prevMessagesRef.current];

        const newMessages = messagesList.filter(newMessage => {
          const existsInPrevious = previousMessages.some(prevMessage => prevMessage.id === newMessage.id);
          const isActive = !newMessage.isArchived;

          return !existsInPrevious && isActive;
        });

        if (newMessages.length > 0) {
          newMessages.forEach(message => {
            notifyNewMessage(message);
            playNotificationSound();
          });
        }
      } else {
        isFirstLoadRef.current.messages = false;
      }

      // Actualizar contador de badges
      if (view !== 'messages') {
        setNewItemsCount(prev => ({ ...prev, messages: countActiveMessages(messagesList) }));
      }

      prevMessagesRef.current = messagesList;
      setAllMessages(messagesList);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to messages: ", error);
      showMessage('Error escuchando mensajes');
      setLoading(false);
    });
  }, [view, notifyNewMessage, playNotificationSound, countActiveMessages, showMessage]);

  const handleSearchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const ordersCollection = collection(db, 'pedidos');
      const ordersQuery = query(ordersCollection, orderBy('date', 'desc'), limit(DASHBOARD_QUERY_LIMIT));
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersList = ordersSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            orderNumber: data.orderNumber,
            ...data,
            date: data.date?.toDate(),
            cartItems: data.cartItems?.map(item => ({
              ...item,
              image: item.images && item.images[0] ? item.images[0] : ''
            })),
            isConfirmed: data.isConfirmed || false,
            isPaid: data.isPaid === true
          };
        })
        .filter(order =>
          order.orderNumber.toString().includes(searchOrder) &&
          order.nombre.toLowerCase().includes(searchName.toLowerCase()) &&
          (order.isActive !== false) === !showArchivedOrders
        );
      setOrders(ordersList);
    } catch (error) {
      console.error("Error fetching orders: ", error);
      showMessage('Error fetching orders');
    }
    setLoading(false);
  }, [searchOrder, searchName, showArchivedOrders, showMessage]);

  const handleSearchSale = useCallback(async () => {
    setLoading(true);
    try {
      const salesCollection = collection(db, 'compras_mercadopago');
      const salesQuery = query(salesCollection, orderBy('date', 'desc'), limit(DASHBOARD_QUERY_LIMIT));
      const salesSnapshot = await getDocs(salesQuery);
      const salesList = salesSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            orderNumber: data.orderNumber,
            ...data,
            date: data.date?.toDate(),
            cartItems: data.cartItems?.map(item => ({
              ...item,
              image: item.images && item.images[0] ? item.images[0] : ''
            })),
            isPaid: data.isPaid || false,
            isConfirmed: data.isConfirmed || false
          };
        })
        .filter(sale =>
          sale.orderNumber.toString().includes(searchOrder) &&
          sale.nombre.toLowerCase().includes(searchName.toLowerCase()) &&
          (sale.isActive !== false) === !showArchivedSales
        );
      setSales(salesList);
    } catch (error) {
      console.error("Error fetching sales: ", error);
      showMessage('Error fetching sales');
    }
    setLoading(false);
  }, [searchOrder, searchName, showArchivedSales, showMessage]);

  const handleSearchMessage = useCallback(async () => {
    setLoading(true);
    try {
      const messagesCollection = collection(db, 'contacts');
      const messagesQuery = query(messagesCollection, orderBy('timestamp', 'desc'), limit(DASHBOARD_QUERY_LIMIT));
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesList = messagesSnapshot.docs
        .map(mapContactDocument)
        .filter(message => {
          const matchesEmail = message.email.toLowerCase().includes(searchOrder.toLowerCase());
          const matchesName = message.name.toLowerCase().includes(searchName.toLowerCase());

          const isArchived = message.isArchived || false;
          const matchesArchiveStatus = showArchivedMessages ? isArchived : !isArchived;

          return matchesEmail && matchesName && matchesArchiveStatus;
        });
      setMessages(messagesList);
    } catch (error) {
      console.error("Error fetching messages: ", error);
      showMessage('Error fetching messages');
    }
    setLoading(false);
  }, [searchOrder, searchName, showArchivedMessages, showMessage]);

  // Configurar listeners en tiempo real y solicitar permisos (SOLO UNA VEZ)
  useEffect(() => {
    if (listenersInitialized) return;

    const initializeNotifications = async () => {
      await requestNotificationPermission();
      setListenersInitialized(true);
    };

    initializeNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listeners en tiempo real: solo con sesión Firebase lista (evita permission-denied si Auth aún no restauró)
  useEffect(() => {
    if (!listenersInitialized) return;

    const detachRealtime = () => {
      if (unsubscribeOrdersRef.current) {
        unsubscribeOrdersRef.current();
        unsubscribeOrdersRef.current = null;
      }
      if (unsubscribeSalesRef.current) {
        unsubscribeSalesRef.current();
        unsubscribeSalesRef.current = null;
      }
      if (unsubscribeMessagesRef.current) {
        unsubscribeMessagesRef.current();
        unsubscribeMessagesRef.current = null;
      }
    };

    const syncListeners = () => {
      detachRealtime();
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }
      const searching = !!(searchOrder || searchName);
      if (searching) {
        if (view === 'orders') {
          handleSearchOrder();
        } else if (view === 'sales') {
          handleSearchSale();
        } else if (view === 'messages') {
          handleSearchMessage();
        }
        return;
      }
      setupOrdersListener();
      setupSalesListener();
      setupMessagesListener();
    };

    syncListeners();
    const unsubAuth = onAuthStateChanged(auth, () => {
      syncListeners();
    });

    return () => {
      unsubAuth();
      detachRealtime();
    };
  }, [
    listenersInitialized,
    searchOrder,
    searchName,
    view,
    handleSearchOrder,
    handleSearchSale,
    handleSearchMessage,
    setupOrdersListener,
    setupSalesListener,
    setupMessagesListener
  ]);

  const clearNotificationCount = (type) => {
    setNewItemsCount(prev => ({ ...prev, [type]: 0 }));
    // Limpiar también el badge de la app cuando se ve una sección
    setTimeout(updateAppBadge, 100);
  };

  const hideOrder = async (orderId, collectionName) => {
    if (window.confirm('¿Estás seguro de que quieres archivar este pedido?')) {
      setOrderActionLoading({ id: orderId, source: collectionName, action: 'hide' });
      try {
        await updateDoc(doc(db, collectionName, orderId), { isActive: false });
        showMessage('Pedido archivado');
      } catch (error) {
        console.error("Error archiving order: ", error);
        showMessage('Error archivando pedido');
      } finally {
        setOrderActionLoading(null);
      }
    }
  };

  const restoreOrder = async (orderId, collectionName) => {
    if (window.confirm('¿Estás seguro de que quieres restaurar este pedido?')) {
      setOrderActionLoading({ id: orderId, source: collectionName, action: 'restore' });
      try {
        await updateDoc(doc(db, collectionName, orderId), { isActive: true });
        showMessage('Pedido restaurado');
      } catch (error) {
        console.error("Error restoring order: ", error);
        showMessage('Error restaurando pedido');
      } finally {
        setOrderActionLoading(null);
      }
    }
  };

  const toggleOrderPaid = async (orderId, collectionName, currentPaid) => {
    setOrderActionLoading({ id: orderId, source: collectionName, action: 'togglePaid' });
    try {
      await updateDoc(doc(db, collectionName, orderId), {
        isPaid: !currentPaid,
        paidUpdatedAt: new Date()
      });
      showMessage(currentPaid ? 'Marcado como pendiente de pago' : 'Marcado como pagado');
    } catch (error) {
      console.error("Error actualizando estado de pago: ", error);
      showMessage('Error al actualizar el estado de pago');
    } finally {
      setOrderActionLoading(null);
    }
  };

  const confirmOrder = async (order, collectionName) => {
    if (window.confirm('¿Estás seguro de que quieres confirmar este pedido?')) {
      setOrderActionLoading({ id: order.id, source: collectionName, action: 'confirm' });
      try {
        const batch = writeBatch(db);

        for (const item of order.cartItems) {
          const productRef = doc(db, 'products', item.id);
          batch.update(productRef, {
            stock: increment(-item.quantity),
            sales: increment(item.quantity)
          });
        }

        await batch.commit();
        await updateDoc(doc(db, collectionName, order.id), { isConfirmed: true });
        showMessage('Operación exitosa');
      } catch (error) {
        console.error("Error confirming order: ", error);
        showMessage('Error confirmando pedido');
      } finally {
        setOrderActionLoading(null);
      }
    }
  };

  const cancelOrder = async (order, collectionName) => {
    if (window.confirm('¿Estás seguro de que quieres cancelar este pedido? Esto retribuirá el stock y descontará las ventas.')) {
      setOrderActionLoading({ id: order.id, source: collectionName, action: 'cancel' });
      try {
        const batch = writeBatch(db);

        for (const item of order.cartItems) {
          const productRef = doc(db, 'products', item.id);
          batch.update(productRef, {
            stock: increment(item.quantity),
            sales: increment(-item.quantity)
          });
        }

        await batch.commit();
        await updateDoc(doc(db, collectionName, order.id), {
          isConfirmed: false,
          status: 'Cancelado',
          cancelledAt: new Date()
        });
        showMessage('Pedido cancelado');
      } catch (error) {
        console.error("Error canceling order: ", error);
        showMessage('Error cancelando pedido');
      } finally {
        setOrderActionLoading(null);
      }
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este mensaje?')) {
      try {
        await deleteDoc(doc(db, 'contacts', messageId));
        showMessage('Mensaje eliminado');
      } catch (error) {
        console.error("Error deleting message: ", error);
        showMessage('Error eliminando mensaje');
      }
    }
  };

  const toggleArchiveMessage = async (messageId) => {
    try {
      const messageRef = doc(db, 'contacts', messageId);
      const messageDoc = await getDoc(messageRef);
      const isArchived = messageDoc.data().isArchived;

      await updateDoc(messageRef, { isArchived: !isArchived });
      showMessage(isArchived ? 'Mensaje restaurado' : 'Mensaje archivado');
    } catch (error) {
      console.error("Error toggling message archive status: ", error);
      showMessage('Error cambiando el estado del mensaje');
    }
  };

  const toggleOrderDetails = (orderId) => {
    setSelectedOrder(selectedOrder === orderId ? null : orderId);
  };

  // Calcular estadísticas
  const getOrderStats = () => {
    const confirmed = orders.filter(o => o.isConfirmed).length;
    const pending = orders.filter(o => !o.isConfirmed).length;
    const totalRevenue = orders
      .filter(o => o.isConfirmed)
      .reduce((sum, o) => sum + (o.totalPrice || 0), 0);

    return {
      total: orders.length,
      confirmed,
      pending,
      totalRevenue
    };
  };

  const getSalesStats = () => {
    const paid = sales.filter(s => s.isPaid).length;
    const unpaid = sales.filter(s => !s.isPaid).length;
    const confirmed = sales.filter(s => s.isConfirmed).length;
    const totalRevenue = sales
      .filter(s => s.isPaid)
      .reduce((sum, s) => sum + (s.totalPrice || 0), 0);

    return {
      total: sales.length,
      paid,
      unpaid,
      confirmed,
      totalRevenue
    };
  };

  const getMessageStats = () => {
    // Usar allMessages en lugar de messages para obtener estadísticas completas
    const archived = allMessages.filter(m => m.isArchived || false).length;
    const active = allMessages.filter(m => !(m.isArchived || false)).length;

    return {
      total: allMessages.length,
      active,
      archived
    };
  };

  const renderStats = () => {
    let stats = {};
    let title = '';

    if (view === 'orders') {
      stats = getOrderStats();
      title = '📦 Estadísticas de Pedidos';
    } else if (view === 'sales') {
      stats = getSalesStats();
      title = '💰 Estadísticas de Ventas MercadoPago';
    } else if (view === 'messages') {
      stats = getMessageStats();
      title = '💬 Estadísticas de Mensajes';
    }

    return (
      <div className="admin-stats">
        <h3>{title}</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.total || 0}</div>
            <div className="stat-label">Total</div>
          </div>

          {view === 'orders' && (
            <>
              <div className="stat-card">
                <div className="stat-number">{stats.confirmed}</div>
                <div className="stat-label">Confirmados</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.pending}</div>
                <div className="stat-label">Pendientes</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{formatPrice(stats.totalRevenue)}</div>
                <div className="stat-label">Ingresos Confirmados</div>
              </div>
            </>
          )}

          {view === 'sales' && (
            <>
              <div className="stat-card">
                <div className="stat-number">{stats.paid}</div>
                <div className="stat-label">Pagados</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.unpaid}</div>
                <div className="stat-label">Sin Pagar</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{stats.confirmed}</div>
                <div className="stat-label">Confirmados</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{formatPrice(stats.totalRevenue)}</div>
                <div className="stat-label">Ingresos Totales</div>
              </div>
            </>
          )}

          {view === 'messages' && (
            <>
              <div 
                className={`stat-card ${messageFilter === false ? 'stat-card-active' : ''}`}
                onClick={() => setMessageFilter(false)}
                style={{ cursor: 'pointer' }}
                title="Mostrar solo mensajes activos"
              >
                <div className="stat-number">{stats.active}</div>
                <div className="stat-label">Activos</div>
              </div>
              <div 
                className={`stat-card ${messageFilter === true ? 'stat-card-active' : ''}`}
                onClick={() => setMessageFilter(true)}
                style={{ cursor: 'pointer' }}
                title="Mostrar solo mensajes archivados"
              >
                <div className="stat-number">{stats.archived}</div>
                <div className="stat-label">Archivados</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderWelcome = () => {
    const showOrd = hasStaffFeature('adminOrders');
    const showSal = hasStaffFeature('adminSalesMp');
    const showMsg = hasStaffFeature('adminMessages');
    return (
      <div className="welcome-section">
        <div className="quick-stats">
          {showOrd && (
          <div
            className="stat-card pending clickable"
            onClick={() => {
              changeView('orders');
              clearNotificationCount('orders');
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-number">{newItemsCount.orders}</div>
              <div className="stat-label">Pedidos Pendientes</div>
            </div>
          </div>
          )}

          {showSal && (
          <div
            className="stat-card pending clickable"
            onClick={() => {
              changeView('sales');
              clearNotificationCount('sales');
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-number">{newItemsCount.sales}</div>
              <div className="stat-label">Ventas Pendientes</div>
            </div>
          </div>
          )}

          {showMsg && (
          <div
            className="stat-card pending clickable"
            onClick={() => {
              changeView('messages');
              clearNotificationCount('messages');
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">💬</div>
            <div className="stat-info">
              <div className="stat-number">{newItemsCount.messages}</div>
              <div className="stat-label">Mensajes Nuevos</div>
            </div>
          </div>
          )}
        </div>

        {/* 🆕 NUEVO: Botón de prueba de notificaciones */}
        {/* <button
          onClick={testAllNotifications}
          className="btn btn-outline"
          style={{marginTop: '1rem'}}
        >
          🧪 Probar Notificaciones
        </button> */}
      </div>
    );
  };

  const renderOrders = () => {
    return (
      <section className="admin-section">
        <div className="section-header">
          <h2>📦 Pedidos Recibidos</h2>
        </div>

        {renderStats()}

        <div className="section-controls">
          <div className="filter-toggle">
            <button
              onClick={() => setShowArchivedOrders(!showArchivedOrders)}
              className={`toggle-btn ${showArchivedOrders ? 'active' : ''}`}
            >
              {showArchivedOrders ? '📁 Mostrar Activos' : '🗄️ Mostrar Archivados'}
            </button>
          </div>

          <div className="search-controls">
            <input
              type="text"
              placeholder="🔍 Buscar por número de pedido..."
              value={searchOrder}
              onChange={(e) => setSearchOrder(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="👤 Buscar por nombre del cliente..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <Spinner />
            <p>Cargando pedidos...</p>
          </div>
        ) : orders.length > 0 ? (
          <div className="orders-container">
            {orders.map(order => (
              <div key={order.id} className={`order-card ${order.isPaid ? 'paid' : (order.isConfirmed ? 'confirmed' : 'unpaid')}`}>
                <div className="order-card-header">
                  <div className="order-info">
                    <h3>Pedido #{order.orderNumber}</h3>
                    <p
                      className={`order-customer-name${getOrderCustomerName(order) ? '' : ' is-empty'}`}
                      title={getOrderCustomerName(order) || 'Sin nombre'}
                    >
                      {getOrderCustomerName(order) || 'Sin nombre'}
                    </p>
                    <div className="order-meta">
                      <div className="order-meta-facts">
                        <span className="order-date">📅 {order.date?.toLocaleDateString()}</span>
                        <span className="order-total">💰 {formatPrice(order.totalPrice)}</span>
                      </div>
                      <div className="order-meta-flags">
                      <span className={`payment-status ${order.isPaid ? 'paid' : 'unpaid'}`} title="Gestión interna de pago">
                        {order.isPaid ? '💳 Pagado' : '⏳ Pendiente de pago'}
                      </span>
                      {(order.trackingSent || order.trackingEmailSentAt) && (
                        <span
                          className="tracking-status-sent"
                          title={order.trackingNumber
                            ? `Seguimiento enviado: ${order.trackingNumber}`
                            : 'N° de guía enviado al cliente'}
                        >
                          📦 N° de guía enviado
                        </span>
                      )}
                      {!(order.trackingSent || order.trackingEmailSentAt) && order.sendTrackingEmail && (
                        <span className="tracking-status-pending" title="El email de seguimiento se está enviando">
                          📦 Enviando seguimiento…
                        </span>
                      )}
                      {order.isConfirmed && (
                        <span className="order-status confirmed" title="Pedido confirmado">
                          ✅ Operación exitosa
                        </span>
                      )}
                      </div>
                    </div>
                  </div>

                  <div className="order-actions">
                    <div className="order-actions-group">
                      <button
                        onClick={() => toggleOrderDetails(order.id)}
                        className="btn btn-outline"
                        disabled={!!orderActionLoading}
                      >
                        {selectedOrder === order.id ? 'Ocultar' : 'Detalles'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openCustomerWhatsApp(order, 'order')}
                        className="btn btn-whatsapp"
                        title={getCustomerPhone(order)
                          ? `Abrir WhatsApp: ${getCustomerPhone(order)}`
                          : 'Este pedido no tiene teléfono'}
                        disabled={!!orderActionLoading || !getCustomerPhone(order)}
                      >
                        💬 WhatsApp
                      </button>
                    </div>
                    <div className="order-actions-group">
                      <button
                        onClick={() => handlePrintOrder(order, 'order')}
                        className="btn btn-secondary"
                        title="Imprimir Rotulo para envios"
                        disabled={!!orderActionLoading}
                      >
                        🖨️ Rótulo
                      </button>
                      <button
                        onClick={() => openRemitoModal(order, 'pedidos')}
                        className="btn btn-secondary"
                        title="Imprimir Remito de Transporte"
                        disabled={!!orderActionLoading || remitoLoadingId === order.id}
                      >
                        {remitoLoadingId === order.id ? '⏳ Generando…' : '🚚 Remito'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setTrackingModal({ item: order, source: 'pedidos' })}
                        className={orderTrackingWasSent(order) ? 'btn btn-outline btn-done btn-tracking' : 'btn btn-secondary btn-tracking'}
                        title={orderTrackingWasSent(order)
                          ? `Número de seguimiento ya enviado${order.trackingNumber ? `: ${order.trackingNumber}` : ''}`
                          : 'Cargar número de seguimiento y enviarlo al cliente'}
                        disabled={!!orderActionLoading}
                      >
                        {orderTrackingWasSent(order) ? '📦 Seguimiento ✓' : '📦 Seguimiento'}
                      </button>
                      <button
                        onClick={() => openNotesModal(order, 'pedidos')}
                        className={order.adminNotes ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                        title={order.adminNotes ? 'Ver/editar nota interna' : 'Agregar nota interna'}
                        disabled={!!orderActionLoading}
                      >
                        {order.adminNotes ? '📝 Notas ✓' : '📝 Notas'}
                      </button>
                      {!isViewerFromStorage() && (
                        <button
                          type="button"
                          onClick={() => setOrderEmailModal({ item: order, source: 'pedidos' })}
                          className={order.lastAdminCustomerEmailAt ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                          title={order.lastAdminCustomerEmailAt ? 'Enviar otro email al cliente' : 'Enviar email al cliente con plantilla Priball'}
                          disabled={!!orderActionLoading}
                        >
                          {order.lastAdminCustomerEmailAt ? '📧 Email ✓' : '📧 Email'}
                        </button>
                      )}
                      <button
                        onClick={() => openInvoiceModal(order, 'pedidos')}
                        className={order.invoiceData ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                        title={order.invoiceData ? 'Factura enviada' : 'Imprimir Factura'}
                        disabled={!!orderActionLoading}
                      >
                        {order.invoiceData ? '🖨️ Factura ✓' : '🖨️ Factura'}
                      </button>
                      {order.invoiceData &&
                        Number(order.invoiceData.tipo_comprobante) === 11 &&
                        order.creditNoteData?.cae && (
                          <button
                            type="button"
                            onClick={() => openInvoiceModal(order, 'pedidos')}
                            className="btn btn-outline btn-done"
                            title="Nota de crédito emitida (AFIP); ver o imprimir desde el comprobante"
                            disabled={!!orderActionLoading}
                          >
                            {'\u2713'} NC ✓
                          </button>
                        )}
                      {order.invoiceData &&
                        Number(order.invoiceData.tipo_comprobante) === 11 &&
                        !order.creditNoteData?.cae && (
                          <button
                            type="button"
                            onClick={() => {
                              setCreditNoteQuickModal({ item: order, type: 'pedidos' });
                              setAdminInvoiceEmail(order.email || '');
                              setInvoiceError('');
                            }}
                            className="btn btn-secondary"
                            title="Emitir Nota de Crédito C (AFIP); email opcional"
                            disabled={!!orderActionLoading || generatingCreditNote}
                          >
                            NC AFIP
                          </button>
                        )}
                    </div>
                    <div className="order-actions-group">
                      <button
                        onClick={() => toggleOrderPaid(order.id, 'pedidos', order.isPaid)}
                        className={order.isPaid ? 'btn btn-outline' : 'btn btn-success'}
                        title={order.isPaid ? 'Marcar como pendiente de pago' : 'Marcar como pagado (gestión interna)'}
                        disabled={!!orderActionLoading}
                      >
                        {order.isPaid ? '↩️ Impago' : '💵 Pagado'}
                      </button>
                      {!order.isConfirmed ? (
                        <button
                          onClick={() => confirmOrder(order, 'pedidos')}
                          className="btn btn-success"
                          title="Marcar como operación completada"
                          disabled={!!orderActionLoading}
                        >
                          ✅ Confirmar
                        </button>
                      ) : (
                        <button
                          onClick={() => cancelOrder(order, 'pedidos')}
                          className="btn btn-warning"
                          title="Revertir a pendiente"
                          disabled={!!orderActionLoading}
                        >
                          ❌ Cancelar
                        </button>
                      )}
                      <button
                        onClick={() => (order.isActive !== false) ? hideOrder(order.id, 'pedidos') : restoreOrder(order.id, 'pedidos')}
                        className="btn btn-outline"
                        disabled={!!orderActionLoading}
                      >
                        {(order.isActive !== false) ? '🗄️ Archivar' : '📁 Restaurar'}
                      </button>
                    </div>
                  </div>
                </div>

                {selectedOrder === order.id && (
                  <div className="order-details">
                    <OrderCustomerInfo
                      item={order}
                      collectionName="pedidos"
                      showMessage={showMessage}
                    />

                    <div className="products-info">
                      <h4>🛍️ Productos</h4>
                      <div className="products-list">
                        {order.cartItems?.map((item, index) => (
                          <div key={index} className="product-item">
                            {item.image && (
                              <img src={item.image} alt={item.model} className="product-image" />
                            )}
                            <div className="product-details">
                              <strong>{item.model}</strong>
                              <div className="product-quantity">Cantidad: {item.quantity}</div>
                              <div className="product-price">Precio: {formatPrice(item.price)}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="order-totals">
                        <div><strong>Subtotal:</strong> {formatPrice(order.subtotal)}</div>
                        {order.discountAmount > 0 && (
                          <div><strong>Descuento:</strong> -{formatPrice(order.discountAmount)} ({order.discountDetail})</div>
                        )}
                        <div className="total"><strong>Total:</strong> {formatPrice(order.totalPrice)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <h3>📭 No hay pedidos para mostrar</h3>
            <p>
              {showArchivedOrders
                ? 'No hay pedidos archivados.'
                : 'No se encontraron pedidos activos.'
              }
            </p>
          </div>
        )}
      </section>
    );
  };

  const renderSales = () => {
    return (
      <section className="admin-section">
        <div className="section-header">
          <h2>💰 Ventas con MercadoPago</h2>
          <p>Gestiona las ventas procesadas con MercadoPago</p>
        </div>

        {renderStats()}

        <div className="section-controls">
          <div className="filter-toggle">
            <button
              onClick={() => setShowArchivedSales(!showArchivedSales)}
              className={`toggle-btn ${showArchivedSales ? 'active' : ''}`}
            >
              {showArchivedSales ? '📁 Mostrar Activos' : '🗄️ Mostrar Archivados'}
            </button>
          </div>

          <div className="search-controls">
            <input
              type="text"
              placeholder="🔍 Buscar por número de venta..."
              value={searchOrder}
              onChange={(e) => setSearchOrder(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="👤 Buscar por nombre del cliente..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <Spinner />
            <p>Cargando ventas...</p>
          </div>
        ) : sales.length > 0 ? (
          <div className="orders-container">
            {sales.map(sale => (
              <div key={sale.id} className={`order-card ${sale.isConfirmed ? 'confirmed' : (sale.isPaid ? 'paid' : 'unpaid')}`}>
                <div className="order-card-header">
                  <div className="order-info">
                    <h3>Venta MP #{sale.orderNumber}</h3>
                    <p
                      className={`order-customer-name${getOrderCustomerName(sale) ? '' : ' is-empty'}`}
                      title={getOrderCustomerName(sale) || 'Sin nombre'}
                    >
                      {getOrderCustomerName(sale) || 'Sin nombre'}
                    </p>
                    <div className="order-meta">
                      <div className="order-meta-facts">
                        <span className="order-date">📅 {sale.date?.toLocaleDateString()}</span>
                        <span className="order-total">💰 {formatPrice(sale.totalPrice)}</span>
                      </div>
                      <div className="order-meta-flags">
                      {sale.isPaid ? (
                        <span className="payment-status paid">💳 Pagado</span>
                      ) : (
                        <span className="payment-status unpaid">⏳ Pendiente pago</span>
                      )}
                      {(sale.trackingSent || sale.trackingEmailSentAt) && (
                        <span
                          className="tracking-status-sent"
                          title={sale.trackingNumber
                            ? `Seguimiento enviado: ${sale.trackingNumber}`
                            : 'N° de guía enviado al cliente'}
                        >
                          📦 N° de guía enviado
                        </span>
                      )}
                      {!(sale.trackingSent || sale.trackingEmailSentAt) && sale.sendTrackingEmail && (
                        <span className="tracking-status-pending" title="El email de seguimiento se está enviando">
                          📦 Enviando seguimiento…
                        </span>
                      )}
                      </div>
                    </div>
                  </div>

                  <div className="order-actions">
                    <div className="order-actions-group">
                      <button
                        onClick={() => toggleOrderDetails(sale.id)}
                        className="btn btn-outline"
                        disabled={!!orderActionLoading}
                      >
                        {selectedOrder === sale.id ? 'Ocultar' : 'Detalles'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openCustomerWhatsApp(sale, 'sale')}
                        className="btn btn-whatsapp"
                        title={getCustomerPhone(sale)
                          ? `Abrir WhatsApp: ${getCustomerPhone(sale)}`
                          : 'Esta venta no tiene teléfono'}
                        disabled={!!orderActionLoading || !getCustomerPhone(sale)}
                      >
                        💬 WhatsApp
                      </button>
                    </div>
                    <div className="order-actions-group">
                      <button
                        onClick={() => handlePrintOrder(sale, 'sale')}
                        className="btn btn-secondary"
                        title="Imprimir Rotulo para envios"
                        disabled={!!orderActionLoading}
                      >
                        🖨️ Rótulo
                      </button>
                      <button
                        onClick={() => openRemitoModal(sale, 'compras_mercadopago')}
                        className="btn btn-secondary"
                        title="Imprimir Remito de Transporte"
                        disabled={!!orderActionLoading || remitoLoadingId === sale.id}
                      >
                        {remitoLoadingId === sale.id ? '⏳ Generando…' : '🚚 Remito'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setTrackingModal({ item: sale, source: 'compras_mercadopago' })}
                        className={orderTrackingWasSent(sale) ? 'btn btn-outline btn-done btn-tracking' : 'btn btn-secondary btn-tracking'}
                        title={orderTrackingWasSent(sale)
                          ? `Número de seguimiento ya enviado${sale.trackingNumber ? `: ${sale.trackingNumber}` : ''}`
                          : 'Cargar número de seguimiento y enviarlo al cliente'}
                        disabled={!!orderActionLoading}
                      >
                        {orderTrackingWasSent(sale) ? '📦 Seguimiento ✓' : '📦 Seguimiento'}
                      </button>
                      <button
                        onClick={() => openNotesModal(sale, 'compras_mercadopago')}
                        className={sale.adminNotes ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                        title={sale.adminNotes ? 'Ver/editar nota interna' : 'Agregar nota interna'}
                        disabled={!!orderActionLoading}
                      >
                        {sale.adminNotes ? '📝 Notas ✓' : '📝 Notas'}
                      </button>
                      {!isViewerFromStorage() && (
                        <button
                          type="button"
                          onClick={() => setOrderEmailModal({ item: sale, source: 'compras_mercadopago' })}
                          className={sale.lastAdminCustomerEmailAt ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                          title={sale.lastAdminCustomerEmailAt ? 'Enviar otro email al cliente' : 'Enviar email al cliente con plantilla Priball'}
                          disabled={!!orderActionLoading}
                        >
                          {sale.lastAdminCustomerEmailAt ? '📧 Email ✓' : '📧 Email'}
                        </button>
                      )}
                      <button
                        onClick={() => openInvoiceModal(sale, 'compras_mercadopago')}
                        className={sale.invoiceData ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                        title={sale.invoiceData ? 'Factura enviada' : 'Imprimir Factura'}
                        disabled={!!orderActionLoading}
                      >
                        {sale.invoiceData ? '🖨️ Factura ✓' : '🖨️ Factura'}
                      </button>
                      {sale.invoiceData &&
                        Number(sale.invoiceData.tipo_comprobante) === 11 &&
                        sale.creditNoteData?.cae && (
                          <button
                            type="button"
                            onClick={() => openInvoiceModal(sale, 'compras_mercadopago')}
                            className="btn btn-outline btn-done"
                            title="Nota de crédito emitida (AFIP); ver o imprimir desde el comprobante"
                            disabled={!!orderActionLoading}
                          >
                            {'\u2713'} NC ✓
                          </button>
                        )}
                      {sale.invoiceData &&
                        Number(sale.invoiceData.tipo_comprobante) === 11 &&
                        !sale.creditNoteData?.cae && (
                          <button
                            type="button"
                            onClick={() => {
                              setCreditNoteQuickModal({ item: sale, type: 'compras_mercadopago' });
                              setAdminInvoiceEmail(sale.email || '');
                              setInvoiceError('');
                            }}
                            className="btn btn-secondary"
                            title="Emitir Nota de Crédito C (AFIP); email opcional"
                            disabled={!!orderActionLoading || generatingCreditNote}
                          >
                            NC AFIP
                          </button>
                        )}
                    </div>
                    <div className="order-actions-group">
                      {!sale.isConfirmed ? (
                        <button
                          onClick={() => confirmOrder(sale, 'compras_mercadopago')}
                          className="btn btn-success"
                          title="Marcar como operación completada"
                          disabled={!!orderActionLoading}
                        >
                          ✅ Confirmar
                        </button>
                      ) : (
                        <button
                          onClick={() => cancelOrder(sale, 'compras_mercadopago')}
                          className="btn btn-warning"
                          title="Revertir a pendiente"
                          disabled={!!orderActionLoading}
                        >
                          ❌ Cancelar
                        </button>
                      )}
                      <button
                        onClick={() => (sale.isActive !== false) ? hideOrder(sale.id, 'compras_mercadopago') : restoreOrder(sale.id, 'compras_mercadopago')}
                        className="btn btn-outline"
                        disabled={!!orderActionLoading}
                      >
                        {(sale.isActive !== false) ? '🗄️ Archivar' : '📁 Restaurar'}
                      </button>
                    </div>
                  </div>
                </div>

                {selectedOrder === sale.id && (
                  <div className="order-details">
                    <OrderCustomerInfo
                      item={sale}
                      collectionName="compras_mercadopago"
                      showMessage={showMessage}
                    />

                    <div className="products-info">
                      <h4>🛍️ Productos</h4>
                      <div className="products-list">
                        {sale.cartItems?.map((item, index) => (
                          <div key={index} className="product-item">
                            {item.image && (
                              <img src={item.image} alt={item.model} className="product-image" />
                            )}
                            <div className="product-details">
                              <strong>{item.model}</strong>
                              <div className="product-quantity">Cantidad: {item.quantity}</div>
                              <div className="product-price">Precio: {formatPrice(item.price)}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="order-totals">
                        <div><strong>Subtotal:</strong> {formatPrice(sale.subtotal)}</div>
                        {sale.discountAmount > 0 && (
                          <div><strong>Descuento:</strong> -{formatPrice(sale.discountAmount)} ({sale.discountDetail})</div>
                        )}
                        <div className="total"><strong>Total:</strong> {formatPrice(sale.totalPrice)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <h3>📭 No hay ventas para mostrar</h3>
            <p>
              {showArchivedSales
                ? 'No hay ventas archivadas.'
                : 'No se encontraron ventas activas.'
              }
            </p>
          </div>
        )}
      </section>
    );
  };

  const renderMessages = () => {
    const composeEmail = (composeMessage.email || '').trim();
    const composeSubject = (composeMessage.subject || '').trim();
    const composeText = (composeMessage.text || '').trim();
    const composeEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const composeCanSend =
      !!composeEmail &&
      composeEmailRegex.test(composeEmail) &&
      !!composeSubject &&
      !!composeText;

    return (
      <section className="admin-section">
        <div className="section-header">
          <h2>💬 Mensajes de Contacto</h2>
          <p>Gestiona los mensajes recibidos desde el formulario de contacto</p>
        </div>

        {renderStats()}

        <div className="section-controls">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setComposeMessageOpen((prev) => !prev)}
          >
            {composeMessageOpen ? 'Cerrar redacción' : '✍️ Escribir nuevo mensaje'}
          </button>
        </div>

        {composeMessageOpen && (
          <div className="ai-reply-container">
            <div className="ai-template-row">
              <div className="ai-template-select-wrapper">
                <label className="ai-reply-label">Plantillas guardadas</label>
                <div className="ai-template-select-row">
                  <select
                    className="ai-template-select"
                    value={composeSelectedTemplateId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      setComposeSelectedTemplateId(newId);
                      if (!newId) return;
                      const tpl = contactTemplates.find(t => t.id === newId);
                      if (!tpl) return;
                      const body = tpl.body || '';
                      const defaultIds = Array.isArray(tpl.defaultProductIds) ? tpl.defaultProductIds : [];
                      setComposeMessage((prev) => ({
                        ...prev,
                        text: body,
                        selectedProductIds: defaultIds.length
                          ? [...defaultIds]
                          : (Array.isArray(prev.selectedProductIds) ? prev.selectedProductIds : [])
                      }));
                    }}
                    disabled={contactTemplatesLoading}
                  >
                    <option value="">{contactTemplatesLoading ? 'Cargando plantillas…' : 'Seleccionar plantilla'}</option>
                    {contactTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name || 'Sin nombre'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline btn-small"
                    onClick={() =>
                      openNewTemplateModalWithDefaults({
                        baseText: (composeMessage.text || '').trim(),
                        defaultProducts: Array.isArray(composeMessage.selectedProductIds)
                          ? [...composeMessage.selectedProductIds]
                          : [],
                        sourceMessageId: null
                      })
                    }
                    disabled={!((composeMessage.text || '').trim())}
                  >
                    Guardar como plantilla
                  </button>
                </div>
              </div>
            </div>
            <div className="search-controls">
              <input
                type="text"
                placeholder="👤 Nombre (opcional)"
                value={composeMessage.name}
                onChange={(e) => setComposeMessage((prev) => ({ ...prev, name: e.target.value }))}
                className="search-input"
              />
              <input
                type="email"
                placeholder="📧 Email del destinatario"
                value={composeMessage.email}
                onChange={(e) => setComposeMessage((prev) => ({ ...prev, email: e.target.value }))}
                className="search-input"
              />
              <input
                type="text"
                placeholder="📝 Asunto"
                value={composeMessage.subject}
                onChange={(e) => setComposeMessage((prev) => ({ ...prev, subject: e.target.value }))}
                className="search-input"
              />
            </div>
            <textarea
              className="ai-reply-textarea"
              placeholder="Escribí el mensaje a enviar..."
              value={composeMessage.text}
              onChange={(e) => setComposeMessage((prev) => ({ ...prev, text: e.target.value }))}
              rows={6}
            />
            <div className="ai-reply-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImproveComposeMessageWithAi}
                disabled={composeMessage.improvingAi || composeMessage.sending}
              >
                {composeMessage.improvingAi ? 'Mejorando con IA...' : '✨ Mejorar mensaje con IA'}
              </button>
            </div>
            <div className="ai-reply-preview-section">
              <label className="ai-reply-label">Productos en presupuesto (email/PDF)</label>
              {contactProductsLoading && (
                <div className="ai-reply-preview-loading">Cargando catálogo…</div>
              )}
              {!contactProductsLoading &&
                Array.isArray(composeMessage.selectedProductIds) &&
                composeMessage.selectedProductIds.length > 0 && (
                <div className="ai-reply-selected-products">
                  <div className="ai-reply-preview-grid">
                    {composeMessage.selectedProductIds.map((prodId) => {
                      const p = allContactProducts.find((x) => x.id === prodId);
                      const img = p?.displayImageUrl;
                      return (
                        <button
                          key={prodId}
                          type="button"
                          className="ai-reply-preview-card selected"
                          onClick={() => {
                            setComposeMessage((prev) => {
                              const current = Array.isArray(prev.selectedProductIds)
                                ? prev.selectedProductIds
                                : [];
                              return {
                                ...prev,
                                selectedProductIds: current.filter((id) => id !== prodId)
                              };
                            });
                          }}
                        >
                          {img && (
                            <img
                              src={img}
                              alt={p.model || p.name || ''}
                              className="ai-reply-preview-img"
                            />
                          )}
                          <span className="ai-reply-preview-name">
                            {p ? (p.model || p.name || 'Producto') : prodId}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="ai-reply-extra-products">
                <button
                  type="button"
                  className="btn btn-outline btn-small"
                  onClick={() => {
                    setProductPickerTargetMessageId('__compose__');
                    setProductPickerOpen(true);
                  }}
                >
                  + Agregar productos
                </button>
              </div>
            </div>
            <div className="ai-reply-send-actions">
              <label className="ai-reply-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={composeMessage.includePdf}
                  onChange={(e) => setComposeMessage((prev) => ({ ...prev, includePdf: e.target.checked }))}
                />
                Adjuntar presupuesto PDF
              </label>
              <button
                type="button"
                className="btn btn-success compose-send-btn"
                onClick={handleSendNewContactMessage}
                disabled={composeMessage.sending || !composeCanSend}
                title={!composeCanSend ? 'Completá email válido, asunto y mensaje' : 'Enviar mensaje'}
              >
                {composeMessage.sending ? 'Enviando...' : '✉️ Enviar mensaje'}
              </button>
            </div>
            {composeMessage.error && (
              <div className="ai-reply-error">{composeMessage.error}</div>
            )}
          </div>
        )}

        <div className="section-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="📧 Buscar por email..."
              value={searchOrder}
              onChange={(e) => setSearchOrder(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="👤 Buscar por nombre..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <Spinner />
            <p>Cargando mensajes...</p>
          </div>
        ) : messages.length > 0 ? (
          <div className="messages-container">
            {messages.map(message => {
              const sentReplies = getContactRepliesForDisplay(message);
              return (
              <div key={message.id} className={`message-card ${message.isArchived ? 'archived' : 'active'}`}>
                <div className="message-header">
                  <div className="message-info">
                    <h3>👤 {message.name}</h3>
                    <div className="message-meta">
                      <span className="message-email">
                        📧 {message.email}
                        <button
                          onClick={() => copyToClipboard(message.email)}
                          className="copy-btn"
                          title="Copiar email"
                        >
                          📋
                        </button>
                      </span>
                      <span className="message-date">📅 {message.timestamp?.toLocaleString()}</span>
                      <span className={`message-status ${message.isArchived ? 'archived' : 'active'}`}>
                        {message.isArchived ? '🗄️ Archivado' : '📬 Activo'}
                      </span>
                      {message.lastAdminReplyAt && (
                        <span className="message-status replied">
                          📄 Respondido {message.lastAdminReplyHasPdf ? 'con PDF' : 'por email'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="message-actions">
                    <button
                      onClick={() => toggleArchiveMessage(message.id)}
                      className="btn btn-outline"
                    >
                      {message.isArchived ? '📁 Restaurar' : '🗄️ Archivar'}
                    </button>
                    <button
                      onClick={() => deleteMessage(message.id)}
                      className="btn btn-danger"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>

                <div className="message-content">
                  <h4>{message.isOutbound ? '✉️ Mensaje iniciado desde panel:' : '💬 Mensaje:'}</h4>
                  {message.subject && (
                    <p><strong>Asunto:</strong> {message.subject}</p>
                  )}
                  <p>{message.message}</p>
                  {sentReplies.length > 0 && (
                      <div className="admin-sent-replies">
                        <h4>✉️ Respuestas enviadas</h4>
                        <ul className="admin-sent-replies-list">
                          {sentReplies.map((r, idx) => (
                            <li key={idx} className="admin-sent-reply-item">
                              <div className="admin-sent-reply-meta">
                                {r.subject && (
                                  <span className="admin-sent-reply-pdf" title="Asunto del email">
                                    {r.subject}
                                  </span>
                                )}
                                {r.sentAt instanceof Date && (
                                  <span className="admin-sent-reply-date">
                                    {r.sentAt.toLocaleString('es-AR')}
                                  </span>
                                )}
                                {r.hasPdf && (
                                  <span className="admin-sent-reply-pdf" title="Incluía presupuesto PDF">
                                    PDF
                                  </span>
                                )}
                              </div>
                              <div className="admin-sent-reply-body">{r.text}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                  )}
                  <div className="ai-reply-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setAiReplies(prev => {
                          const prevState = prev[message.id] || {};
                          return {
                            ...prev,
                            [message.id]: {
                              ...prevState,
                              isOpen: !prevState.isOpen
                            }
                          };
                        });
                      }}
                    >
                      {aiReplies[message.id]?.isOpen ? 'Cerrar respuesta' : 'Responder'}
                    </button>
                    {aiReplies[message.id]?.isOpen && (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleGenerateAiReply(message)}
                        disabled={!!(aiReplies[message.id]?.loading)}
                      >
                        {aiReplies[message.id]?.loading ? 'Generando respuesta IA...' : '💡 Generar respuesta IA'}
                      </button>
                    )}
                  </div>
                  {aiReplies[message.id]?.isOpen && (
                    <div className="ai-reply-container">
                      <div className="ai-template-row">
                        <div className="ai-template-select-wrapper">
                          <label className="ai-reply-label">Plantillas guardadas</label>
                          <div className="ai-template-select-row">
                            <select
                              className="ai-template-select"
                              value={selectedTemplateId}
                              onChange={(e) => {
                                const newId = e.target.value;
                                setSelectedTemplateId(newId);
                                if (!newId) return;
                                const tpl = contactTemplates.find(t => t.id === newId);
                                if (!tpl) return;
                                const body = tpl.body || '';
                                const defaultIds = Array.isArray(tpl.defaultProductIds) ? tpl.defaultProductIds : [];
                                setAiReplies(prev => ({
                                  ...prev,
                                  [message.id]: {
                                    ...(prev[message.id] || {}),
                                    text: body,
                                    selectedProductIds: defaultIds.length
                                      ? [...defaultIds]
                                      : (prev[message.id]?.selectedProductIds || [])
                                  }
                                }));
                              }}
                              disabled={contactTemplatesLoading}
                            >
                              <option value="">{contactTemplatesLoading ? 'Cargando plantillas…' : 'Seleccionar plantilla'}</option>
                              {contactTemplates.map((tpl) => (
                                <option key={tpl.id} value={tpl.id}>
                                  {tpl.name || 'Sin nombre'}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-outline btn-small"
                              onClick={() => openNewTemplateModal(message)}
                            >
                              Guardar como plantilla
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline btn-small"
                              onClick={openEditTemplateModal}
                              disabled={!selectedTemplateId}
                            >
                              Editar plantilla
                            </button>
                          </div>
                        </div>
                      </div>
                      {(aiReplies[message.id]?.text || aiReplies[message.id]?.error) && (
                      <>
                        {aiReplies[message.id]?.error && (
                          <div className="ai-reply-error">
                            {aiReplies[message.id].error}
                          </div>
                        )}
                        {aiReplies[message.id]?.text && (
                          <>
                          <div className="ai-reply-header-row">
                            <label className="ai-reply-label">
                              ✍️ Respuesta sugerida por IA (editable antes de enviar):
                            </label>
                            <button
                              type="button"
                              className="ai-reply-expand-btn"
                              onClick={() => setAiReplyFullscreenMessageId(message.id)}
                            >
                              Ver en pantalla completa
                            </button>
                          </div>
                          <textarea
                            className="ai-reply-textarea"
                            value={aiReplies[message.id].text}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAiReplies(prev => ({
                                ...prev,
                                [message.id]: {
                                  ...(prev[message.id] || {}),
                                  text: value
                                }
                              }));
                            }}
                            rows={6}
                          />
                          </>
                        )}
                      </>
                      )}
                      <div className="ai-reply-preview-section">
                        <label className="ai-reply-label">Productos en presupuesto (email/PDF)</label>
                        {contactProductsLoading && (
                          <div className="ai-reply-preview-loading">Cargando catálogo…</div>
                        )}
                        {!contactProductsLoading &&
                          Array.isArray(aiReplies[message.id]?.selectedProductIds) &&
                          aiReplies[message.id].selectedProductIds.length > 0 && (
                          <div className="ai-reply-selected-products">
                            <div className="ai-reply-preview-grid">
                              {aiReplies[message.id].selectedProductIds.map((prodId) => {
                                const p = allContactProducts.find((x) => x.id === prodId);
                                const img = p?.displayImageUrl;
                                return (
                                  <button
                                    key={prodId}
                                    type="button"
                                    className="ai-reply-preview-card selected"
                                    onClick={() => {
                                      setAiReplies((prev) => {
                                        const prevState = prev[message.id] || {};
                                        const current = Array.isArray(prevState.selectedProductIds)
                                          ? prevState.selectedProductIds
                                          : [];
                                        const next = current.filter((id) => id !== prodId);
                                        return {
                                          ...prev,
                                          [message.id]: {
                                            ...prevState,
                                            selectedProductIds: next
                                          }
                                        };
                                      });
                                    }}
                                  >
                                    {img && (
                                      <img
                                        src={img}
                                        alt={p.model || p.name || ''}
                                        className="ai-reply-preview-img"
                                      />
                                    )}
                                    <span className="ai-reply-preview-name">
                                      {p ? (p.model || p.name || 'Producto') : prodId}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="ai-reply-extra-products">
                          <button
                            type="button"
                            className="btn btn-outline btn-small"
                            onClick={() => {
                              setProductPickerTargetMessageId(message.id);
                              setProductPickerOpen(true);
                            }}
                          >
                            + Agregar productos
                          </button>
                        </div>
                      </div>
                      {aiReplies[message.id]?.text && (
                        <>
                          <div className="ai-reply-send-actions">
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleSendContactReply(message, { includePdf: false })}
                              disabled={!!(aiReplies[message.id]?.sending)}
                            >
                              {aiReplies[message.id]?.sending ? 'Enviando...' : '✉️ Enviar solo respuesta'}
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={() => handleSendContactReply(message, { includePdf: true })}
                              disabled={!!(aiReplies[message.id]?.sending)}
                            >
                              {aiReplies[message.id]?.sending ? 'Enviando...' : '📄 Enviar presupuesto (PDF)'}
                            </button>
                          </div>
                          {aiReplies[message.id]?.sendError && (
                            <div className="ai-reply-error">
                              {aiReplies[message.id].sendError}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <h3>📭 No hay mensajes para mostrar</h3>
            <p>
              {showArchivedMessages
                ? 'No hay mensajes archivados.'
                : 'No se encontraron mensajes activos.'
              }
            </p>
          </div>
        )}
      </section>
    );
  };

  const staffPermFull = isFullAdminFromStorage();
  const staffPermList = getStaffPermissionsFromStorage();
  const canAccessClientesEditor =
    staffPermFull ||
    hasStaffFeature('customers') ||
    hasStaffFeature('localSales') ||
    hasStaffFeature('adminOrders') ||
    hasStaffFeature('adminSalesMp');
  const canAccessProveedoresEditor =
    staffPermFull ||
    hasStaffFeature('suppliers') ||
    hasStaffFeature('localSales') ||
    hasStaffFeature('adminOrders') ||
    hasStaffFeature('adminSalesMp');
  const showToolsGrid = showToolsGridFromFeatures(staffPermFull, staffPermList);
  const showOnlineSec = showOnlineSectionFromFeatures(staffPermFull, staffPermList);
  const readOnlyPanel = isViewerFromStorage();
  const showSalesAndBalanceGroup =
    hasStaffFeature('localSales') ||
    hasStaffFeature('salesHistory') ||
    hasStaffFeature('analytics') ||
    hasStaffFeature('balance') ||
    hasStaffFeature('expenses') ||
    canAccessClientesEditor ||
    canAccessProveedoresEditor;
  const showMarketingGroup =
    hasStaffFeature('subscribers') ||
    hasStaffFeature('newsletter') ||
    hasStaffFeature('discounts') ||
    hasStaffFeature('shippingCosts');
  const showProductionGroup =
    hasStaffFeature('stock') ||
    hasStaffFeature('massEditor') ||
    hasStaffFeature('factory') ||
    staffPermFull;

  const hasPcOpsRail = showToolsGrid && (showMarketingGroup || showProductionGroup);
  const hasPcSalesStrip = showToolsGrid && showSalesAndBalanceGroup;

  return (
    <div
      className={[
        'admin-dashboard',
        hasPcOpsRail && 'admin-dashboard--rail',
        hasPcSalesStrip && 'admin-dashboard--sales-strip'
      ].filter(Boolean).join(' ')}
    >
      {readOnlyPanel && (
        <div className="admin-dashboard__read-only-banner" role="status">
          Modo solo lectura: podés consultar el panel pero no guardar cambios en la base de datos.
        </div>
      )}
      {orderActionLoading && <Spinner />}
      {/* Header Principal */}
      <div className="dashboard-header">
        {/* Contenedor de controles */}
        <div className="header-controls">
          <div className="header-title-block">
            <h1>🏪 Panel de Control</h1>
            <p>Gestiona tu tienda de forma eficiente y profesional</p>
          </div>

          {/* Botones de control */}
          <div className="header-buttons">
            {/* Botón para silenciar notificaciones */}
            <button
              onClick={toggleNotifications}
              className={`mute-notifications-btn ${notificationsMuted ? 'muted' : ''}`}
              title={notificationsMuted ? 'Activar notificaciones' : 'Silenciar notificaciones'}
            >
              {notificationsMuted ? '🔇' : '🔔'}
            </button>

          </div>
        </div>
      </div>

      <div className="admin-dashboard__main">
      {/* Enlaces de Navegación */}
      {showToolsGrid && (
      <div className="nav-links-section">
        <h2 className="nav-links-section__title">🔧 Herramientas de Gestión</h2>
        <div className="nav-links-grid">
          {showSalesAndBalanceGroup && (
            <div className="admin-dashboard__sales-anchor">
            <section className="tool-group tool-group-sales">
              <h3 className="tool-group-title">Ventas y Balances</h3>
              <div className="tool-group-grid">
                {hasStaffFeature('localSales') && (
                <Link to="/local" className="nav-link round physical-sales">
                  <div className="link-icon">🏪</div>
                  <div className="link-text">
                    <h3>Ventas Físicas</h3>
                    <p>Registra ventas en local</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('salesHistory') && (
                <Link to="/historial-ventas" className="nav-link round reports-history">
                  <div className="link-icon">📋</div>
                  <div className="link-text">
                    <h3>Historial de Ventas</h3>
                    <p>Revisa el historial</p>
                  </div>
                </Link>
                )}

                {canAccessClientesEditor && (
                <Link to="/admin/clientes" className="nav-link round customers-editor">
                  <div className="link-icon">👤</div>
                  <div className="link-text">
                    <h3>Clientes</h3>
                    <p>Editar datos de contacto</p>
                  </div>
                </Link>
                )}

                {canAccessProveedoresEditor && (
                <Link to="/admin/proveedores" className="nav-link round suppliers-editor">
                  <div className="link-icon">🏭</div>
                  <div className="link-text">
                    <h3>Proveedores</h3>
                    <p>Historial de compras por proveedor</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('analytics') && (
                <Link to="/analiticas" className="nav-link round reports-analytics">
                  <div className="link-icon">📊</div>
                  <div className="link-text">
                    <h3>Analíticas</h3>
                    <p>Analiza tus ventas</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('balance') && (
                <Link to="/balance" className="nav-link round reports-balance">
                  <div className="link-icon">📈</div>
                  <div className="link-text">
                    <h3>Balance</h3>
                    <p>Visualiza estadísticas</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('expenses') && (
                <Link to="/admin/gastos" className="nav-link round expenses">
                  <div className="link-icon">💸</div>
                  <div className="link-text">
                    <h3>Gastos y Cheques</h3>
                    <p>Gestión administrativa</p>
                  </div>
                </Link>
                )}
              </div>
            </section>
            </div>
          )}

          {(showMarketingGroup || showProductionGroup) && (
            <div className="admin-dashboard__ops-anchor">
            <section className="tool-group tool-group-ops">
              <h3 className="tool-group-title">Herramientas</h3>
              <div className="tool-group-grid">
                {hasStaffFeature('subscribers') && (
                <Link to="/subscriptores" className="nav-link uniform-tile subscribers">
                  <div className="link-icon">👥</div>
                  <div className="link-text">
                    <h3>Suscriptores</h3>
                    <p>Lista de suscriptores</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('newsletter') && (
                <Link to="/newsletter" className="nav-link uniform-tile newsletter">
                  <div className="link-icon">✉️</div>
                  <div className="link-text">
                    <h3>Newsletter</h3>
                    <p>Gestión de envíos masivos</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('discounts') && (
                <Link to="/configuracion-descuentos" className="nav-link uniform-tile discounts">
                  <div className="link-icon">🏷️</div>
                  <div className="link-text">
                    <h3>Descuentos</h3>
                    <p>Configura promociones</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('shippingCosts') && (
                <Link to="/costo-envios" className="nav-link uniform-tile shipping">
                  <div className="link-icon">🚚</div>
                  <div className="link-text">
                    <h3>Costos de Envío</h3>
                    <p>Gestiona tarifas</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('stock') && (
                <Link to="/gestion-stock" className="nav-link uniform-tile stock">
                  <div className="link-icon">📦</div>
                  <div className="link-text">
                    <h3>Gestión Stock</h3>
                    <p>Controla tu inventario</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('massEditor') && (
                <Link to="/editor-masivo" className="nav-link uniform-tile editor">
                  <div className="link-icon">⚡</div>
                  <div className="link-text">
                    <h3>Editor Masivo</h3>
                    <p>Edita productos en lote</p>
                  </div>
                </Link>
                )}

                {hasStaffFeature('factory') && (
                <Link to="/factory" className="nav-link uniform-tile factory">
                  <div className="link-icon">🏭</div>
                  <div className="link-text">
                    <h3>Gestión de Producción</h3>
                    <p>Control de fabricación</p>
                  </div>
                </Link>
                )}

                {staffPermFull && (
                <Link to="/admin/usuarios" className="nav-link uniform-tile user-roles">
                  <div className="link-icon">🔐</div>
                  <div className="link-text">
                    <h3>Usuarios y permisos</h3>
                    <p>Cuentas, roles y accesos al panel</p>
                  </div>
                </Link>
                )}
              </div>
            </section>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Sección de Ventas Online */}
      {showOnlineSec && (
      <div className="online-sales-section">
        <div className="section-header">
          <h2>🌐 Ventas Online</h2>
        </div>

        {/* Navegación de Vistas */}
        <div className="view-navigation">
          {hasStaffFeature('adminMercadoPago') && (
          <button
            onClick={() => changeView('mercadopago')}
            className={`nav-btn ${view === 'mercadopago' ? 'active' : ''}`}
          >
            💳 MercadoPago Admin
          </button>
          )}
          {hasStaffFeature('adminOrders') && (
          <button
            onClick={() => {
              changeView('orders');
              clearNotificationCount('orders');
            }}
            className={`nav-btn ${view === 'orders' ? 'active' : ''}`}
          >
            📦 Pedidos Recibidos
            <NotificationBadge count={newItemsCount.orders} />
          </button>
          )}
          {hasStaffFeature('adminSalesMp') && (
          <button
            onClick={() => {
              changeView('sales');
              clearNotificationCount('sales');
            }}
            className={`nav-btn ${view === 'sales' ? 'active' : ''}`}
          >
            💰 Ventas MercadoPago
            <NotificationBadge count={newItemsCount.sales} />
          </button>
          )}

          {hasStaffFeature('adminMessages') && (
          <button
            onClick={() => {
              changeView('messages');
              clearNotificationCount('messages');
            }}
            className={`nav-btn ${view === 'messages' ? 'active' : ''}`}
          >
            💬 Mensajes
            <NotificationBadge count={newItemsCount.messages} />
          </button>
          )}
        </div>

        {/* Contenido de las Vistas */}
        <div className="view-content">
          {view === '' && renderWelcome()}
          {view === 'orders' && renderOrders()}
          {view === 'sales' && renderSales()}
          {view === 'mercadopago' && <MercadoPagoAdmin />}
          {view === 'messages' && renderMessages()}
        </div>
      </div>
      )}

      </div>

      {/* Notificaciones */}
      <Notification message={notification.message} show={notification.show} />

      {/* Modal de respuesta IA en pantalla completa */}
      {aiReplyFullscreenMessageId && aiReplies[aiReplyFullscreenMessageId]?.text && (
        <div className="ai-reply-fullscreen-overlay" onClick={() => setAiReplyFullscreenMessageId(null)}>
          <div
            className="ai-reply-fullscreen-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-reply-fullscreen-header">
              <h3>Editar respuesta IA</h3>
              <button
                type="button"
                className="ai-reply-fullscreen-close"
                onClick={() => setAiReplyFullscreenMessageId(null)}
              >
                ✕
              </button>
            </div>
            <textarea
              className="ai-reply-fullscreen-textarea"
              value={aiReplies[aiReplyFullscreenMessageId].text}
              onChange={(e) => {
                const value = e.target.value;
                setAiReplies((prev) => ({
                  ...prev,
                  [aiReplyFullscreenMessageId]: {
                    ...(prev[aiReplyFullscreenMessageId] || {}),
                    text: value
                  }
                }));
              }}
            />
          </div>
        </div>
      )}

      {/* Modal del Remito */}
      {showRemitoModal && remitoOrderInfo && (
        <div 
          className="remito-modal-overlay" 
          onClick={closeRemitoModal}
        >
          <div 
            className="remito-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="remito-modal-header">
              <h3>Remito de Transporte</h3>
              <button onClick={closeRemitoModal} className="remito-modal-close">✕</button>
            </div>
            <RemitoPrintInline saleInfo={remitoOrderInfo} />
          </div>
        </div>
      )}

      {orderEmailModal && (
        <OrderCustomerEmailModal
          item={orderEmailModal.item}
          source={orderEmailModal.source}
          onClose={() => setOrderEmailModal(null)}
          showMessage={showMessage}
        />
      )}

      {trackingModal && (
        <TrackingNumberModal
          item={trackingModal.item}
          source={trackingModal.source}
          onClose={() => setTrackingModal(null)}
          showMessage={showMessage}
          onSaved={(fields) => {
            const apply = (row) => (row.id === trackingModal.item.id ? { ...row, ...fields } : row);
            if (trackingModal.source === 'compras_mercadopago') {
              setSales((prev) => prev.map(apply));
            } else {
              setOrders((prev) => prev.map(apply));
            }
          }}
        />
      )}

      {/* Modal de Notas internas (pedidos y ventas MercadoPago) */}
      {showNotesModal && notesContext && (() => {
        const { item } = notesContext;
        const isViewer = isViewerFromStorage();
        const updatedAtRaw = item.adminNotesUpdatedAt;
        const updatedAt = updatedAtRaw?.toDate?.()
          ?? (updatedAtRaw ? new Date(updatedAtRaw) : null);
        const orderLabel = item.orderNumber || (item.id ? item.id.slice(0, 6) : '');
        return (
          <div className="notes-modal-overlay" onClick={closeNotesModal}>
            <div className="notes-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="notes-modal-header">
                <h3>Notas {orderLabel ? `· #${orderLabel}` : ''}</h3>
                <button
                  onClick={closeNotesModal}
                  className="notes-modal-close"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
              <textarea
                className="notes-textarea"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Escribí una anotación interna sobre este pedido…"
                rows={8}
                readOnly={isViewer}
                disabled={notesSaving}
                autoFocus
              />
              {updatedAt && (
                <p className="notes-meta">
                  Última actualización: {updatedAt.toLocaleString('es-AR')}
                  {item.adminNotesUpdatedBy ? ` · ${item.adminNotesUpdatedBy}` : ''}
                </p>
              )}
              <div className="notes-modal-actions">
                <button
                  onClick={closeNotesModal}
                  className="btn btn-outline"
                  disabled={notesSaving}
                >
                  {isViewer ? 'Cerrar' : 'Cancelar'}
                </button>
                {!isViewer && (
                  <button
                    onClick={saveNotes}
                    className="btn btn-success"
                    disabled={notesSaving}
                  >
                    {notesSaving ? 'Guardando…' : '💾 Guardar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Facturación: reutiliza ImprimirTicket (mismo que LocalSales) + opción de crear factura AFIP */}
      {invoiceModalItem && (() => {
        const saleInfo = mapOrderOrSaleToSaleInfo(invoiceModalItem.item, invoiceModalItem.type);
        const hasInvoice = !!saleInfo.invoiceData;
        const isFacturaC = Number(saleInfo.invoiceData?.tipo_comprobante) === 11;
        const canIssueCreditNote = hasInvoice && isFacturaC && !saleInfo.creditNoteData?.cae;
        return (
          <div className="local-sales-modal invoice-modal" onClick={closeInvoiceModal}>
            <div className="local-sales-modal-content invoice-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="invoice-modal-header">
                <h3>Facturación</h3>
                <button onClick={closeInvoiceModal} className="close-modal-btn">✕</button>
              </div>

              {/* Si no tiene factura AFIP: opción de crear (obtener datos necesarios) */}
              {!hasInvoice && (
                <div className="admin-invoice-generate-form">
                  <p style={{ color: '#ecf0f1', marginBottom: 12 }}>
                    Crear factura AFIP (CAE + QR) para este pedido/venta. Completá los datos:
                  </p>
                  <div className="admin-invoice-fields">
                    <label>
                      <span>Punto de venta:</span>
                      <input
                        type="number"
                        min={1}
                        max={9999}
                        value={adminPuntoVenta}
                        onChange={(e) => setAdminPuntoVenta(Number(e.target.value) || 8)}
                        style={{ width: 80, padding: 6, marginLeft: 8 }}
                      />
                    </label>
                    <label>
                      <span>Tipo:</span>
                      <select
                        value={adminInvoiceType}
                        onChange={(e) => setAdminInvoiceType(e.target.value)}
                        style={{ padding: 6, marginLeft: 8 }}
                      >
                        <option value="facturaC">Factura C</option>
                        <option value="facturaB">Factura B</option>
                        <option value="facturaA">Factura A</option>
                      </select>
                    </label>
                    <label>
                      <span>Email (opcional, se envía automático):</span>
                      <input
                        type="email"
                        placeholder={saleInfo.customerData?.email || 'email@ejemplo.com'}
                        value={adminInvoiceEmail}
                        onChange={(e) => setAdminInvoiceEmail(e.target.value)}
                        style={{ padding: 6, marginLeft: 8, minWidth: 200 }}
                      />
                    </label>
                    {adminInvoiceType === 'facturaC' && (
                      <label style={{ display: 'block', marginTop: 8 }}>
                        <span>CUIT/DNI del comprador {Number(saleInfo.totalPrice) >= montoMaxSinCuit ? '(obligatorio para este monto - AFIP)' : '(opcional)'}:</span>
                        <input
                          type="text"
                          placeholder={Number(saleInfo.totalPrice) >= montoMaxSinCuit ? '20-12345678-9 o DNI' : (saleInfo.customerData?.cuit || 'para factura con dato fiscal')}
                          value={adminInvoiceCuit}
                          onChange={(e) => setAdminInvoiceCuit(e.target.value)}
                          style={{ padding: 6, marginLeft: 8, minWidth: 200 }}
                          maxLength={15}
                        />
                      </label>
                    )}
                  </div>
                  {invoiceError && (
                    <div style={{ color: '#e74c3c', marginTop: 12, padding: 10, background: 'rgba(231,76,60,0.1)', borderRadius: 8 }}>
                      {invoiceError}
                    </div>
                  )}
                  {invoiceProgress && (
                    <div style={{ color: '#3498db', marginTop: 12 }}>
                      {generatingInvoice && <Spinner />} {invoiceProgress}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => generateAfipInvoiceFromAdmin(saleInfo)}
                    disabled={generatingInvoice || generatingCreditNote}
                    className="btn btn-success"
                    style={{ marginTop: 12, marginBottom: 16 }}
                  >
                    {generatingInvoice ? 'Generando...' : 'Crear factura AFIP'}
                  </button>
                </div>
              )}

              {hasInvoice && canIssueCreditNote && (
                <div className="admin-invoice-generate-form" style={{ marginBottom: 16 }}>
                  <p style={{ color: '#ecf0f1', marginBottom: 12 }}>
                    Emitir <strong>Nota de Crédito C</strong> en AFIP por anulación total de la factura{' '}
                    <strong>{saleInfo.invoiceData?.numero || ''}</strong>. El importe debe coincidir con la factura.
                  </p>
                  <div className="admin-invoice-fields">
                    <label>
                      <span>Punto de venta:</span>
                      <input
                        type="number"
                        min={1}
                        max={9999}
                        value={adminPuntoVenta}
                        onChange={(e) => setAdminPuntoVenta(Number(e.target.value) || 8)}
                        style={{ width: 80, padding: 6, marginLeft: 8 }}
                      />
                    </label>
                    <label>
                      <span>Email (opcional, envío automático):</span>
                      <input
                        type="email"
                        placeholder={saleInfo.customerData?.email || 'email@ejemplo.com'}
                        value={adminInvoiceEmail}
                        onChange={(e) => setAdminInvoiceEmail(e.target.value)}
                        style={{ padding: 6, marginLeft: 8, minWidth: 200 }}
                      />
                    </label>
                  </div>
                  {invoiceError && (
                    <div style={{ color: '#e74c3c', marginTop: 12, padding: 10, background: 'rgba(231,76,60,0.1)', borderRadius: 8 }}>
                      {invoiceError}
                    </div>
                  )}
                  {invoiceProgress && generatingCreditNote && (
                    <div style={{ color: '#3498db', marginTop: 12 }}>
                      <Spinner /> {invoiceProgress}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => generateAfipCreditNoteFromAdmin(saleInfo, invoiceModalItem.type)}
                    disabled={generatingCreditNote || generatingInvoice}
                    className="btn btn-warning"
                    style={{ marginTop: 12 }}
                  >
                    {generatingCreditNote ? 'Generando NC...' : 'Emitir Nota de Crédito C + email (si hay correo)'}
                  </button>
                </div>
              )}

              {hasInvoice && saleInfo.creditNoteData?.cae && (
                <p style={{ color: '#2ecc71', marginBottom: 12, fontSize: 14 }}>
                  Nota de crédito registrada: {saleInfo.creditNoteData.numero} (CAE {saleInfo.creditNoteData.cae})
                </p>
              )}

              {/* Comprobante: mismo componente que LocalSales (ImprimirTicket); imprimir en ventana nueva para una sola hoja */}
              <div className="admin-invoice-ticket-wrap">
                <ImprimirTicket saleInfo={saleInfo} onPrintClick={printTicketInNewWindow} />
              </div>

              {/* Acciones: Enviar comprobante por email (solo si aún no tiene factura AFIP) */}
              <div className="admin-invoice-actions">
                {!hasInvoice && CONFIG.CLOUD_FUNCTIONS.URLS?.sendOrderInvoiceByEmail && (
                    <button
                      onClick={async () => {
                        const email = prompt('Email para enviar:', saleInfo.customerData?.email || '');
                        if (!email || !email.trim()) return;
                        setSendingInvoiceEmail(true);
                        try {
                          const col = invoiceModalItem.type === 'pedidos' ? 'pedidos' : 'compras_mercadopago';
                          const res = await fetch(CONFIG.CLOUD_FUNCTIONS.URLS.sendOrderInvoiceByEmail, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ collection: col, docId: saleInfo.id, email: email.trim() })
                          });
                          if (res.ok) {
                            showMessage(`Enviado a ${email.trim()}`);
                            closeInvoiceModal();
                          } else {
                            const err = await res.json().catch(() => ({}));
                            showMessage(err.message || 'Error al enviar');
                          }
                        } catch (e) {
                          showMessage('Error: ' + (e.message || 'Sin conexión'));
                        } finally {
                          setSendingInvoiceEmail(false);
                        }
                      }}
                      className="btn btn-secondary"
                      disabled={sendingInvoiceEmail}
                    >
                      {sendingInvoiceEmail ? '⏳ Enviando...' : '📧 Enviar comprobante por Email'}
                    </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {creditNoteQuickModal && (() => {
        const qSaleInfo = mapOrderOrSaleToSaleInfo(creditNoteQuickModal.item, creditNoteQuickModal.type);
        return (
          <div className="local-sales-modal invoice-modal" onClick={() => !generatingCreditNote && setCreditNoteQuickModal(null)}>
            <div className="local-sales-modal-content invoice-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="invoice-modal-header">
                <h3>Nota de crédito AFIP</h3>
                <button type="button" className="close-modal-btn" onClick={() => !generatingCreditNote && setCreditNoteQuickModal(null)}>✕</button>
              </div>
              <p style={{ color: '#ecf0f1', marginBottom: 12 }}>
                Pedido/Venta <strong>#{qSaleInfo.orderNumber}</strong> — Factura {qSaleInfo.invoiceData?.numero}. Anulación total (importe igual a la factura).
              </p>
              <div className="admin-invoice-fields">
                <label>
                  <span>Punto de venta:</span>
                  <input
                    type="number"
                    min={1}
                    max={9999}
                    value={adminPuntoVenta}
                    onChange={(e) => setAdminPuntoVenta(Number(e.target.value) || 8)}
                    style={{ width: 80, padding: 6, marginLeft: 8 }}
                    disabled={generatingCreditNote}
                  />
                </label>
                <label>
                  <span>Email (opcional):</span>
                  <input
                    type="email"
                    value={adminInvoiceEmail}
                    onChange={(e) => setAdminInvoiceEmail(e.target.value)}
                    style={{ padding: 6, marginLeft: 8, minWidth: 200 }}
                    disabled={generatingCreditNote}
                    placeholder={qSaleInfo.customerData?.email || 'email@ejemplo.com'}
                  />
                </label>
              </div>
              {invoiceError && (
                <div style={{ color: '#e74c3c', marginTop: 12, padding: 10, background: 'rgba(231,76,60,0.1)', borderRadius: 8 }}>
                  {invoiceError}
                </div>
              )}
              {invoiceProgress && generatingCreditNote && (
                <div style={{ color: '#3498db', marginTop: 12 }}>
                  <Spinner /> {invoiceProgress}
                </div>
              )}
              <button
                type="button"
                className="btn btn-warning"
                style={{ marginTop: 12 }}
                disabled={generatingCreditNote}
                onClick={() => generateAfipCreditNoteFromAdmin(qSaleInfo, creditNoteQuickModal.type)}
              >
                {generatingCreditNote ? 'Generando...' : 'Emitir NC + email si corresponde'}
              </button>
            </div>
          </div>
        );
      })()}

      {aiReplyFullscreenMessageId && aiReplies[aiReplyFullscreenMessageId]?.text && (
        <div className="ai-reply-fullscreen-overlay">
          <div className="ai-reply-fullscreen-content">
            <div className="ai-reply-fullscreen-header">
              <h3>Editar respuesta IA</h3>
              <button
                type="button"
                className="ai-reply-fullscreen-close"
                onClick={() => setAiReplyFullscreenMessageId(null)}
              >
                ✕
              </button>
            </div>
            <textarea
              className="ai-reply-fullscreen-textarea"
              value={aiReplies[aiReplyFullscreenMessageId].text}
              onChange={(e) => {
                const value = e.target.value;
                const id = aiReplyFullscreenMessageId;
                setAiReplies(prev => ({
                  ...prev,
                  [id]: {
                    ...(prev[id] || {}),
                    text: value
                  }
                }));
              }}
            />
          </div>
        </div>
      )}

      {productPickerOpen && (
        <div
          className="product-picker-overlay"
          onClick={() => {
            setProductPickerOpen(false);
            setProductPickerTargetMessageId(null);
          }}
        >
          <div
            className="product-picker-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-picker-header">
              <h3>Agregar productos al presupuesto</h3>
              <button
                type="button"
                className="product-picker-close"
                onClick={() => {
                  setProductPickerOpen(false);
                  setProductPickerTargetMessageId(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="product-picker-body">
              <div className="product-picker-filters">
                <select
                  className="product-picker-input"
                  value={productPickerCategoryFilter}
                  onChange={(e) => setProductPickerCategoryFilter(e.target.value)}
                >
                  <option value="">Todas las categorías</option>
                  {Array.from(
                    new Set(
                      allContactProducts
                        .map((p) => (p.category || '').toString().trim())
                        .filter((c) => c)
                    )
                  ).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="product-picker-input"
                  placeholder="Buscar por modelo, nombre o descripción..."
                  value={productPickerSearch}
                  onChange={(e) => setProductPickerSearch(e.target.value)}
                />
              </div>
              {allContactProducts.length === 0 ? (
                <div className="product-picker-empty">
                  No se encontraron productos en la colección Products.
                </div>
              ) : (
                (() => {
                  const cat = productPickerCategoryFilter.trim();
                  const kw = productPickerSearch.trim().toLowerCase();
                  const filtered = allContactProducts.filter((p) => {
                    const catVal = (p.category || '').toString().toLowerCase();
                    if (cat && p.category !== cat) return false;
                    if (!kw) return true;
                    const haystack = [
                      p.model,
                      p.name,
                      p.description,
                      p.category
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .toString()
                      .toLowerCase();
                    return haystack.includes(kw);
                  });
                  if (filtered.length === 0) {
                    return (
                      <div className="product-picker-empty">
                        No hay productos que coincidan con los filtros.
                      </div>
                    );
                  }
                  return (
                <div className="product-picker-grid">
                  {filtered.map((p) => (
                    <div key={p.id} className="product-picker-card">
                      {(() => {
                        const targetId = productPickerTargetMessageId;
                        const selectedIds = targetId === '__compose__'
                          ? (Array.isArray(composeMessage.selectedProductIds) ? composeMessage.selectedProductIds : [])
                          : (Array.isArray(aiReplies[targetId]?.selectedProductIds) ? aiReplies[targetId].selectedProductIds : []);
                        const isAlreadyAdded = selectedIds.includes(p.id);
                        return (
                          <>
                      {p.displayImageUrl ? (
                        <img
                          src={p.displayImageUrl}
                          alt={p.model || p.name || ''}
                          className="product-picker-img"
                        />
                      ) : (
                        <div className="product-picker-card-noimage">
                          Sin imagen
                        </div>
                      )}
                      <div className="product-picker-info">
                        <div className="product-picker-name">
                          {p.model || p.name || 'Producto'}
                        </div>
                        <div className="product-picker-category">
                          {p.category || 'Sin categoría'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`btn btn-small product-picker-add-btn ${isAlreadyAdded ? 'is-added' : 'btn-outline'}`}
                        disabled={isAlreadyAdded}
                        onClick={() => {
                          const targetId = productPickerTargetMessageId;
                          if (!targetId) return;
                          if (targetId === '__compose__') {
                            setComposeMessage((prev) => {
                              const current = Array.isArray(prev.selectedProductIds)
                                ? prev.selectedProductIds
                                : [];
                              if (current.includes(p.id)) return prev;
                              return {
                                ...prev,
                                selectedProductIds: [...current, p.id]
                              };
                            });
                            showMessage('Producto agregado al presupuesto del nuevo mensaje.');
                            return;
                          }
                          setAiReplies((prev) => {
                            const prevState = prev[targetId] || {};
                            const current = Array.isArray(prevState.selectedProductIds)
                              ? prevState.selectedProductIds
                              : [];
                            if (current.includes(p.id)) return prev;
                            const updated = {
                              ...prev,
                              [targetId]: {
                                ...prevState,
                                selectedProductIds: [...current, p.id]
                              }
                            };
                            return updated;
                          });
                          showMessage('Producto agregado al presupuesto de este mensaje.');
                        }}
                      >
                        {isAlreadyAdded ? 'Agregado' : 'Agregar'}
                      </button>
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {templateModalOpen && (
        <div className="template-modal-overlay">
          <div className="template-modal-content">
            <div className="template-modal-header">
              <h3>{templateForm.id ? 'Editar plantilla' : 'Nueva plantilla'}</h3>
              <button
                type="button"
                className="template-modal-close"
                  onClick={() => {
                    setTemplateModalOpen(false);
                    setTemplateSourceMessageId(null);
                  }}
              >
                ✕
              </button>
            </div>
            <div className="template-modal-body">
              <label className="template-modal-label">
                Nombre de la plantilla
                <input
                  type="text"
                  className="template-modal-input"
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm(prev => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Ej: Presupuesto pelotas personalizadas"
                />
              </label>
              <label className="template-modal-label">
                Contenido
                <textarea
                  className="template-modal-textarea"
                  value={templateForm.body}
                  onChange={(e) =>
                    setTemplateForm(prev => ({ ...prev, body: e.target.value }))
                  }
                  rows={12}
                />
              </label>
            </div>
            <div className="template-modal-footer">
              {templateForm.id && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteTemplate}
                >
                  Eliminar
                </button>
              )}
              <div className="template-modal-footer-right">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setTemplateModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveTemplate}
                >
                  Guardar plantilla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;